/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

document.addEventListener('submit', function(event) {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || !form.classList.contains('product-form--shipping-contact-only')) {
        return;
    }

    event.preventDefault();
    event.stopPropagation();
}, true);

// ------------------------------------------------------------------------------------------------------------
// THUMBNAIL TO VARIANT SELECTION FUNCTIONALITY
// ------------------------------------------------------------------------------------------------------------
// This functionality allows clicking on product thumbnails to automatically select the corresponding variant

// Helper: extract variant data from a thumbnail element
document.addEventListener('DOMContentLoaded', function() {
  const productMediaElements = document.querySelectorAll('product-media');

  function updateVariantInUrl(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);
  }

  function updateVariantsInputs(thumbnail) {
    const variantId = thumbnail.getAttribute('data-variant-id');
    if (!variantId) return;

    updateVariantInUrl(variantId);

    const options = ['1', '2', '3'].map(num => {
      const opt = thumbnail.getAttribute(`data-variant-option${num}`);
      return opt
        ? opt.replace(/ /g, '-').replace(/\./g, '-').replace(/\,/g, '').toLowerCase()
        : null;
    }).filter(Boolean);

    let lastInput = null;
    options.forEach(opt => {
      const input = document.querySelector(`variant-picker input[data-option-label="${opt}"]`);
      if (input) {
        input.checked = true;
        lastInput = input;
      }
    });

    if (lastInput) {
      lastInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  productMediaElements.forEach(function(productMedia) {
    const formId = productMedia.getAttribute('form-id');
    if (!formId) return;

    const variantPicker = document.querySelector(`variant-picker[form-id="${formId}"]`);
    if (!variantPicker) return;

    const thumbnails = productMedia.querySelectorAll('[data-thumbnail-variant-selection]');

    thumbnails.forEach(function(thumbnail, index) {
      thumbnail.addEventListener('click', function(event) {
        if (!thumbnail.getAttribute('data-variant-id')) {
          const previousThumbnail = thumbnails[index - 1];
          updateVariantsInputs(previousThumbnail);
        } else {
          updateVariantsInputs(thumbnail);
        }
      });
    });
  });
});
// Function to update product description based on variant ID
function updateProductDescription(variantId) {
  if (!document.querySelector('.product .product-form__description p')){
    return;
  }

  let productDescription = document.querySelector('.product .product-form__description p');

  if (document.querySelector(`[data-description-id="${variantId}"]`)) {
    let description = document.querySelector(`[data-description-id="${variantId}"]`).getAttribute("data-description");
    productDescription.textContent = description;
  } else if (document.querySelector('[data-product-description]').getAttribute("data-product-description") != '') {
    const productDescriptionText = document.querySelector('[data-product-description]').getAttribute("data-product-description");
    productDescription.textContent = productDescriptionText;
  } else {
    productDescription.textContent = '';
  }
}

function showPDPSpecifications(variantId) {
    const specificationsContainer = document.querySelector('[data-pdp-specifications-container]');
    const specificationsList = document.querySelector('[data-pdp-specifications-list]');

    if (!specificationsContainer || !specificationsList || !variantId) {
        return;
    }

    const variantSpecificationsElement = document.querySelector(`[data-pdp-specifications="${variantId}"]`);
    specificationsList.innerHTML = '';

    if (!variantSpecificationsElement) {
        specificationsContainer.setAttribute('hidden', '');
        return;
    }

    let specificationLines = [];

    try {
        specificationLines = JSON.parse(variantSpecificationsElement.textContent.trim() || '[]');
    } catch {
        specificationsContainer.setAttribute('hidden', '');
        return;
    }

    const validLines = specificationLines.filter((line) => typeof line === 'string' && line.trim() !== '');

    if (!validLines.length) {
        specificationsContainer.setAttribute('hidden', '');
        return;
    }

    validLines.forEach((line) => {
        const listItem = document.createElement('li');
        listItem.textContent = line;
        specificationsList.appendChild(listItem);
    });

    specificationsContainer.removeAttribute('hidden');
}

function updateLeadTime(variantId, scopeElement) {
    if (!variantId) { return; }

    let scope = document;
    if (scopeElement) {
        scope = scopeElement.closest('.product-form') || scopeElement;
    }

    const leadTimeElement = scope.querySelector('.product-form__lead-time');
    if (!leadTimeElement) { return; }

    const dataElement = scope.querySelector(`[data-variant-lead-time][data-variant-id="${variantId}"]`);
    const prodTimeLow = dataElement ? dataElement.getAttribute('data-prod-time-low') : '';
    const prodTimeHi = dataElement ? dataElement.getAttribute('data-prod-time-hi') : '';

    if (!prodTimeLow && !prodTimeHi) {
        leadTimeElement.textContent = '';
        leadTimeElement.setAttribute('hidden', '');
        return;
    }

    leadTimeElement.removeAttribute('hidden');
    if (prodTimeLow && prodTimeHi) {
        const rangeTemplate = getTranslatedText('general.production_lead_time.range');
        leadTimeElement.textContent = rangeTemplate
            .replace('@prod_time_low_value@', prodTimeLow)
            .replace('@prod_time_hi_value@', prodTimeHi);
    } else if (prodTimeLow) {
        const lowTemplate = getTranslatedText('general.production_lead_time.low_only');
        leadTimeElement.textContent = lowTemplate.replace('@prod_time_low_value@', prodTimeLow);
    } else {
        const highTemplate = getTranslatedText('general.production_lead_time.high_only');
        leadTimeElement.textContent = highTemplate.replace('@prod_time_hi_value@', prodTimeHi);
    }
}

function updateEmployeeVariantMeta(variantId, scopeElement) {
    if (!variantId) {
        return;
    }

    const formElement = scopeElement instanceof HTMLFormElement ? scopeElement : null;
    const productInfoElement = formElement ? formElement.closest('.product__info') : document.querySelector('.product__info');

    if (!productInfoElement) {
        return;
    }

    const containerElement = productInfoElement.querySelector('[data-customer-variant-meta]');
    const contentElement = productInfoElement.querySelector('[data-customer-variant-meta-content]');
    const metafieldsElement = productInfoElement.querySelector('script[data-customer-variant-metafields]');

    if (!containerElement || !contentElement || !metafieldsElement) {
        return;
    }

    let variantMetafields;

    try {
        variantMetafields = JSON.parse(metafieldsElement.textContent);
    } catch (error) {
        console.error('Error parsing customer variant metafields data:', error);
        return;
    }

    const variantData = variantMetafields[String(variantId)] || {};
    const jdsNumber = typeof variantData.jds_number === 'string' ? variantData.jds_number.trim() : '';
    const employeeNotesList = Array.isArray(variantData.employee_notes) ? variantData.employee_notes.filter(n => typeof n === 'string' && n.trim() !== '') : [];
    const escapeHtml = (value) => String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    const contentParts = [];

    if (jdsNumber) {
        contentParts.push(
            `<p class="product-meta__customer-variant-jds">JDS: <span class="product-meta__customer-variant-jds-number">${escapeHtml(jdsNumber)}</span></p>`
        );
    }

    if (employeeNotesList.length > 0) {
        const listItems = employeeNotesList.map(note => `<li>${escapeHtml(note)}</li>`).join('');
        contentParts.push(
            `<ul class="product-meta__customer-variant-notes">${listItems}</ul>`
        );
    }

    if (contentParts.length === 0) {
        contentElement.innerHTML = '';
        containerElement.hidden = true;
        return;
    }

    contentElement.innerHTML = contentParts.join('');
    containerElement.hidden = false;
}

document.addEventListener('variant:changed', function(event) {
    showPDPSpecifications(event.detail.variant.id);
    let variantId = event.detail.variant.id;
    updateProductDescription(variantId);
    const formElement = document.getElementById(event.detail.formId);
    updateLeadTime(variantId, formElement);
    updateEmployeeVariantMeta(variantId, formElement);
});

// Listen to option input changes to auto-select unselected options
document.addEventListener('DOMContentLoaded', function() {
    if (document.querySelector('[data-selected-variant]')) {
        const selectedVariant = document.querySelector('[data-selected-variant]');
        showPDPSpecifications(selectedVariant.getAttribute('data-selected-variant'));
    }

    document.addEventListener('change', function(event) {
        // Check if this is an option input change
        if (event.target.matches('input[data-option-position][type="radio"]') && event.target.name.includes('option')) {
        const variantPicker = event.target.closest('variant-picker');
        if (variantPicker) {
            const formId = variantPicker.getAttribute('form-id');
            if (formId) {
            setTimeout(() => {
                autoSelectUnselectedOptions(formId);
            }, 200);
            }
        }
        }
    });
});

// Function to automatically select the first available option for unselected positions
function autoSelectUnselectedOptions(formId) {
  if (!formId) return;
  
  // Find the variant-picker for this form-id
  const variantPicker = document.querySelector(`variant-picker[form-id="${formId}"]`);
  if (!variantPicker) return;
  
  // Get all option position numbers using the form attribute
  const allOptionInputs = document.querySelectorAll(`input[data-option-position][form="${formId}"]`);
  const optionPositions = new Set();
  allOptionInputs.forEach(input => {
    optionPositions.add(input.getAttribute('data-option-position'));
  });
  
  // Check each option position to see if one is selected
  optionPositions.forEach(position => {
    const checkedInput = document.querySelector(`input[data-option-position="${position}"][form="${formId}"]:checked`);
    const hiddenInput = document.querySelector(`input[data-option-position="${position}"][type="hidden"][form="${formId}"]`);
    
    // If no input is checked and no hidden input exists, select the first available option
    if (!checkedInput && !hiddenInput) {
      // Find all radio inputs for this position
      const allOptionsForPosition = Array.from(document.querySelectorAll(`input[data-option-position="${position}"][form="${formId}"]`))
        .filter(input => input.type === 'radio');
      
      // Find the first available (non-disabled) option
      const firstAvailableOption = allOptionsForPosition.find(input => {
        // Check if the option is disabled by checking its parent container
        const parent = input.closest('.block-swatch, .color-swatch, .variant-swatch');
        if (parent && parent.classList.contains('is-disabled')) {
          return false;
        }
        return true;
      });
      
      // If we found an available option, select it
      if (firstAvailableOption) {
        firstAvailableOption.checked = true;
        // Dispatch change event to trigger variant update
        firstAvailableOption.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });
}

document.addEventListener('DOMContentLoaded', function() {
  const selectedVariantElement = document.querySelector('[data-selected-or-first-available-variant-id]');
  if (selectedVariantElement) {
    const variantId = selectedVariantElement.getAttribute('data-selected-or-first-available-variant-id');
    updateProductDescription(variantId);
        updateLeadTime(variantId, selectedVariantElement);
  }
});
// ------------------------------------------------------------------------------------------------------------
// PRODUCT DECORATION MODAL FUNCTIONALITY
// ------------------------------------------------------------------------------------------------------------

// Product decoration modal functionality using theme's existing components
document.addEventListener('DOMContentLoaded', function () {
    let elements = {};

    function getElements() {
        if (Object.keys(elements).length === 0) {
            elements = {
                uploadTriggers: document.querySelectorAll('[data-upload-trigger]'),
                dataUploadInstructions: document.querySelector('[data-upload-instruction-text]'),
                uploadArea: document.querySelector('[data-upload-area]'),
                fileList: document.querySelector('[data-file-list]'),
                fileItems: document.querySelector('[data-file-items]'),
                csvUploadTrigger: document.querySelector('[data-csv-upload-trigger]'),
                csvSection: document.querySelector('[data-csv-section]'),
                csvFileList: document.querySelector('[data-csv-file-list]'),
                csvPostPurchaseToggle: document.querySelector('[data-csv-post-purchase-toggle]'),
                csvPostPurchaseInput: document.querySelector('[data-csv-post-purchase-input]'),
                productDecorationModal: document.querySelector('[data-product-decoration-modal]')
            };
        }
        return elements;
    }

    // Initialize all functionality
    function initializeProductDecoration() {
        const elements = getElements();

        if (!elements.productDecorationModal) {
            setTimeout(initializeProductDecoration, 100);
            return;
        }

        initializeFileUpload();
        initializeCSVUpload();
        initializeCsvPostPurchaseToggle();
        initializePriceCalculation();
        initializeColorSelection();
        initializeMobileDecorationDescription();
        methodCardPriceCalculation();
        updatePrice();
        initializeMoreEmbellishmentsProperty();
        showNotesInput();
        initializePrintingOptions();
        initializePictogramOptions();
    }

    // File upload functionality
    function initializeFileUpload() {
        const elements = getElements();
        const designFileInput = document.querySelector('[data-design-file-input]');
        const addNoteTrigger = document.querySelector('[data-add-note-trigger]');
        const uploadInstruction = document.querySelector('[data-upload-instruction-text]');

        if (elements.uploadArea && elements.fileList && elements.fileItems && designFileInput) {
            designFileInput.addEventListener('change', function (e) {
                isFileDialogOpen = false;

                const newFiles = Array.from(e.target.files);
                const validFiles = [];
                const invalidFiles = [];

                // Validate each file size
                newFiles.forEach(file => {
                    const validation = validateFileSize(file, 20);
                    if (validation.isValid) {
                        validFiles.push(file);
                    } else {
                        invalidFiles.push(validation.message);
                    }
                });

                // Show error messages for invalid files
                if (invalidFiles.length > 0) {
                    invalidFiles.forEach(message => {
                        alert(message);
                    });
                }

                // Only add valid files
                if (validFiles.length > 0) {
                    allDesignFiles = [...allDesignFiles, ...validFiles];

                    elements.fileItems.innerHTML = '';

                    allDesignFiles.forEach(file => addFileToList(file));

                    createMultipleFileInputs(allDesignFiles, 'design');

                    if (allDesignFiles.length > 0) {
                        elements.uploadArea.style.display = 'none';
                        elements.fileList.style.display = 'block';
                        addNoteTrigger.removeAttribute('important-hidden');
                        uploadInstruction.textContent = 'If you only have a paper copy, specify that in notes and we will contact you to arrange receipt of the art.';
                    }

                }

                // Clear the file input to allow selecting the same file again
                designFileInput.value = '';
            });

            if (elements.uploadTriggers.length > 0) {
                elements.uploadTriggers.forEach(trigger => {
                    trigger.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!isFileDialogOpen) {
                            isFileDialogOpen = true;
                            designFileInput.click();

                            setTimeout(() => {
                                isFileDialogOpen = false;
                            }, 1000);
                        }
                    });
                });
            }
        }
    }

    // Global array to store all selected design files
    let allDesignFiles = [];
    let allCsvFiles = [];
    let csvFile = null;
    let isFileDialogOpen = false;

    // File size validation helper function
    function validateFileSize(file, maxSizeMB = 20) {
        const maxSizeBytes = maxSizeMB * 1024 * 1024; // Convert MB to bytes
        if (file.size > maxSizeBytes) {
            return {
                isValid: false,
                message: `File "${file.name}" is too large. Maximum file size is ${maxSizeMB}MB.`
            };
        }
        return { isValid: true };
    }

    // Function to create multiple file inputs for multiple files
    function createMultipleFileInputs(files, type) {
        const form = document.querySelector("[data-product-decoration-modal]");
        if (!form) {
            return;
        }

        // Remove existing dynamic inputs of this type
        const existingInputs = form.querySelectorAll(`[data-dynamic-file="true"][data-file-type="${type}"]`);
        existingInputs.forEach(input => input.remove());

        // Create new inputs for each file
        files.forEach((file, index) => {
            // Only create inputs for actual File objects
            if (file instanceof File) {
                const input = document.createElement('input');
                input.type = 'file';
                
                // Set the name based on type
                if (type === 'csv') {
                    input.name = `properties[CSV File ${index + 1}]`;
                } else if (type === 'pictogram') {
                    input.name = `properties[Pictogram File ${index + 1}]`;
                } else if (type === 'printing') {
                    input.name = `properties[Printing File ${index + 1}]`;
                } else {
                    input.name = `properties[Design File ${index + 1}]`;
                }
                
                input.style.display = 'none';
                input.setAttribute('data-dynamic-file', 'true');
                input.setAttribute('data-file-type', type);
                input.setAttribute('data-filename', file.name);

                // Create a new FileList with this file
                const dt = new DataTransfer();
                dt.items.add(file);
                input.files = dt.files;

                form.appendChild(input);
            }
        });
    }

    function addFileToList(file) {
        const elements = getElements();
        const fileItem = document.createElement('div');
        fileItem.className = 'product-decoration-file-item';
        fileItem.innerHTML = createFileItemHTML(file);

        const deleteBtn = fileItem.querySelector('.product-decoration-file-delete');
        deleteBtn.addEventListener('click', function () {
            removeFileFromList(file.name);
        });

        elements.fileItems.appendChild(fileItem);
    }

    function createFileItemHTML(file) {
        return `
      <div class="product-decoration-file-item-wrapper">
        <div class="product-decoration-file-item-content">
          <span class="product-decoration-file-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M16.6143 7.23366C16.606 7.20699 16.5968 7.18088 16.5868 7.15533C16.5473 7.06491 16.492 6.98223 16.4235 6.91116L11.4235 1.91116C11.3524 1.84269 11.2697 1.78738 11.1793 1.74783C11.1543 1.73616 11.1277 1.72949 11.101 1.72033C11.0312 1.69668 10.9587 1.68239 10.8852 1.67783C10.8668 1.67616 10.851 1.66699 10.8335 1.66699H5.00016C4.081 1.66699 3.3335 2.41449 3.3335 3.33366V16.667C3.3335 17.5862 4.081 18.3337 5.00016 18.3337H15.0002C15.9193 18.3337 16.6668 17.5862 16.6668 16.667V7.50033C16.6668 7.48283 16.6577 7.46699 16.656 7.44866C16.6516 7.37547 16.6376 7.30318 16.6143 7.23366ZM13.8218 6.66699H11.6668V4.51199L13.8218 6.66699ZM5.00016 16.667V3.33366H10.0002V7.50033C10.0002 7.72134 10.088 7.9333 10.2442 8.08958C10.4005 8.24586 10.6125 8.33366 10.8335 8.33366H15.0002L15.0018 16.667H5.00016Z" fill="black" fill-opacity="0.7"/>
            </svg>
          </span>
          <span class="product-decoration-file-name">${file.name}</span>
        </div>
        <button type="button" class="product-decoration-file-delete" data-filename="${file.name}">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M4.16667 16.667C4.16667 17.109 4.34226 17.5329 4.65482 17.8455C4.96738 18.1581 5.39131 18.3337 5.83333 18.3337H14.1667C14.6087 18.3337 15.0326 18.1581 15.3452 17.8455C15.6577 17.5329 15.8333 17.109 15.8333 16.667V6.66699H17.5V5.00033H14.1667V3.33366C14.1667 2.89163 13.9911 2.46771 13.6785 2.15515C13.3659 1.84259 12.942 1.66699 12.5 1.66699H7.5C7.05797 1.66699 6.63405 1.84259 6.32149 2.15515C6.00893 2.46771 5.83333 2.89163 5.83333 3.33366V5.00033H2.5V6.66699H4.16667V16.667ZM7.5 3.33366H12.5V5.00033H7.5V3.33366ZM6.66667 6.66699H14.1667V16.667H5.83333V6.66699H6.66667Z" fill="#232323"/>
            <path d="M7.5 8.33301H9.16667V14.9997H7.5V8.33301ZM10.8333 8.33301H12.5V14.9997H10.8333V8.33301Z" fill="#232323"/>
          </svg>
        </button>
      </div>
    `;
    }

    function removeFileFromList(filename) {
        const elements = getElements();
        const addNoteTrigger = document.querySelector('[data-add-note-trigger]');
        const uploadNotes = document.querySelector('[data-notes-section]');

        allDesignFiles = allDesignFiles.filter(file => file.name !== filename);

        elements.fileItems.innerHTML = '';

        allDesignFiles.forEach(file => addFileToList(file));

        createMultipleFileInputs(allDesignFiles, 'design');

        if (allDesignFiles.length === 0) {
            elements.uploadArea.style.display = 'flex';
            elements.fileList.style.display = 'none';
            uploadNotes.setAttribute('hidden', 'true');

            addNoteTrigger.setAttribute('important-hidden', 'true');
            elements.dataUploadInstructions.textContent = 'Art Not Ready? You are welcome to place your order now, even without your design file. Your artwork can be submitted after checkout, at your convenience.';
        }
    }

    // CSV upload functionality
    function initializeCSVUpload() {
        const elements = getElements();
        const csvFileInput = document.querySelector('[data-csv-file-input]');

        if (elements.csvUploadTrigger && elements.csvSection && elements.csvFileList && csvFileInput) {
            csvFileInput.addEventListener('change', function (e) {
                const files = Array.from(e.target.files);
                if (files[0]) {
                    // Validate CSV file size
                    const validation = validateFileSize(files[0], 20);
                    if (validation.isValid) {
                        if (elements.csvPostPurchaseToggle) {
                            elements.csvPostPurchaseToggle.checked = true;
                        }
                        // Store CSV file in global array
                        allCsvFiles = [files[0]];
                        addCsvFileToList(files[0]);
                        createMultipleFileInputs(allCsvFiles, 'csv');
                        syncCsvPersonalizationState();
                    } else {
                        alert(validation.message);
                    }
                }
                // Clear the file input to allow selecting the same file again
                csvFileInput.value = '';
            });
        }
    }

    function syncCsvPersonalizationState() {
        const elements = getElements();
        const hasCsvFile = Array.isArray(allCsvFiles) && allCsvFiles.length > 0;

        if (!elements.csvPostPurchaseInput) {
            return;
        }

        if (elements.csvPostPurchaseToggle?.checked && !hasCsvFile) {
            elements.csvPostPurchaseInput.disabled = false;
            elements.csvPostPurchaseInput.value = window.themeVariables.strings.to_be_provided_later;
        } else {
            elements.csvPostPurchaseInput.disabled = true;
        }

        if (elements.csvSection) {
            elements.csvSection.style.display = hasCsvFile ? 'block' : 'none';
        }
    }

    function initializeCsvPostPurchaseToggle() {
        const elements = getElements();

        if (!elements.csvPostPurchaseToggle || !elements.csvPostPurchaseInput) {
            return;
        }

        const updateCsvPostPurchaseState = () => {
            if (!elements.csvPostPurchaseToggle.checked && allCsvFiles.length > 0) {
                removeCsvFile({ keepSelection: false });
                return;
            }

            syncCsvPersonalizationState();
        };

        elements.csvPostPurchaseToggle.addEventListener('change', updateCsvPostPurchaseState);
        updateCsvPostPurchaseState();
    }

    function addCsvFileToList(file) {
        const elements = getElements();
        csvFile = file;

        const csvFileItem = document.createElement('div');
        csvFileItem.className = 'product-decoration-csv-file-item';
        csvFileItem.innerHTML = createCsvFileItemHTML(file);

        elements.csvFileList.innerHTML = '';
        elements.csvFileList.appendChild(csvFileItem);

        if (elements.csvPostPurchaseToggle) {
            elements.csvPostPurchaseToggle.checked = true;
        }

        elements.csvSection.style.display = 'block';

        const deleteBtn = csvFileItem.querySelector('[data-csv-delete]');
        deleteBtn.addEventListener('click', function () {
            removeCsvFile();
        });

        syncCsvPersonalizationState();
    }

    function createCsvFileItemHTML(file) {
        return `
      <div class="product-decoration-file-item-wrapper">
        <div class="product-decoration-file-item-content">
          <span class="product-decoration-csv-file-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M16.6143 7.23366C16.606 7.20699 16.5968 7.18088 16.5868 7.15533C16.5473 7.06491 16.492 6.98223 16.4235 6.91116L11.4235 1.91116C11.3524 1.84269 11.2697 1.78738 11.1793 1.74783C11.1543 1.73616 11.1277 1.72949 11.101 1.72033C11.0312 1.69668 10.9587 1.68239 10.8852 1.67783C10.8668 1.67616 10.851 1.66699 10.8335 1.66699H5.00016C4.081 1.66699 3.3335 2.41449 3.3335 3.33366V16.667C3.3335 17.5862 4.081 18.3337 5.00016 18.3337H15.0002C15.9193 18.3337 16.6668 17.5862 16.6668 16.667V7.50033C16.6668 7.48283 16.6577 7.46699 16.656 7.44866C16.6516 7.37547 16.6376 7.30318 16.6143 7.23366ZM13.8218 6.66699H11.6668V4.51199L13.8218 6.66699ZM5.00016 16.667V3.33366H10.0002V7.50033C10.0002 7.72134 10.088 7.9333 10.2442 8.08958C10.4005 8.24586 10.6125 8.33366 10.8335 8.33366H15.0002L15.0018 16.667H5.00016Z" fill="black" fill-opacity="0.7"/>
            </svg>
          </span>
          <span class="product-decoration-csv-file-name">${file.name}</span>
        </div>
        <button type="button" class="product-decoration-file-delete" data-csv-filename="${file.name}" data-csv-delete>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M4.16667 16.667C4.16667 17.109 4.34226 17.5329 4.65482 17.8455C4.96738 18.1581 5.39131 18.3337 5.83333 18.3337H14.1667C14.6087 18.3337 15.0326 18.1581 15.3452 17.8455C15.6577 17.5329 15.8333 17.109 15.8333 16.667V6.66699H17.5V5.00033H14.1667V3.33366C14.1667 2.89163 13.9911 2.46771 13.6785 2.15515C13.3659 1.84259 12.942 1.66699 12.5 1.66699H7.5C7.05797 1.66699 6.63405 1.84259 6.32149 2.15515C6.00893 2.46771 5.83333 2.89163 5.83333 3.33366V5.00033H2.5V6.66699H4.16667V16.667ZM7.5 3.33366H12.5V5.00033H7.5V3.33366ZM6.66667 6.66699H14.1667V16.667H5.83333V6.66699H6.66667Z" fill="#232323"/>
            <path d="M7.5 8.33301H9.16667V14.9997H7.5V8.33301ZM10.8333 8.33301H12.5V14.9997H10.8333V8.33301Z" fill="#232323"/>
          </svg>
        </button>
      </div>
    `;
    }

    function removeCsvFile(options = {}) {
        const { keepSelection = true } = options;
        const elements = getElements();

        // Clear CSV files from global array
        allCsvFiles = [];

        // Remove CSV file inputs
        const form = document.querySelector("[data-product-decoration-modal]");
        if (form) {
            const csvInputs = form.querySelectorAll('[data-dynamic-file="true"][data-file-type="csv"]');
            csvInputs.forEach(input => input.remove());
        }

        csvFile = null;
        elements.csvFileList.innerHTML = '';
        elements.csvSection.style.display = 'none';

        // Clear the CSV file input value to allow re-uploading the same file
        const csvFileInput = document.querySelector('[data-csv-file-input]');
        if (csvFileInput) {
            csvFileInput.value = '';
        }

        if (!keepSelection && elements.csvPostPurchaseToggle) {
            elements.csvPostPurchaseToggle.checked = false;
        }

        syncCsvPersonalizationState();
    }

    function getSelectedDecorationMethod() {
        const selectedDecorationMethodInput = document.querySelector('input[name="properties[Decoration Method]"]:checked');
        return selectedDecorationMethodInput ? (selectedDecorationMethodInput.getAttribute('data-method') || selectedDecorationMethodInput.value || '') : '';
    }

    function isDecorationDimensionInputActive(inputElement) {
        if (!(inputElement instanceof HTMLInputElement) || inputElement.disabled || inputElement.closest('[hidden]')) {
            return false;
        }

        if (inputElement.matches('[data-width-input], [data-height-input], [data-width-input-color], [data-height-input-color]')) {
            const selectedMethod = getSelectedDecorationMethod();
            return !!selectedMethod && selectedMethod !== 'Blank';
        }

        return true;
    }

    function clampDecorationDimensionInputValue(inputElement) {
        if (!(inputElement instanceof HTMLInputElement) || inputElement.value === '') {
            updateDecorationDimensionInputValidity(inputElement);
            return false;
        }

        const precisionLimited = limitDecorationDimensionInputPrecision(inputElement);
        const currentValue = parseFloat(inputElement.value);
        if (!Number.isFinite(currentValue)) {
            updateDecorationDimensionInputValidity(inputElement);
            return false;
        }

        const minValue = parseFloat(inputElement.getAttribute('min'));
        const maxValue = parseFloat(inputElement.getAttribute('max'));
        let clampedValue = currentValue;
        let didChange = precisionLimited;

        if (Number.isFinite(minValue) && clampedValue < minValue) {
            clampedValue = minValue;
            didChange = true;
        }

        if (Number.isFinite(maxValue) && clampedValue > maxValue) {
            clampedValue = maxValue;
            didChange = true;
        }

        if (didChange) {
            inputElement.value = formatDecorationDimensionValue(clampedValue);
        }

        updateDecorationDimensionInputValidity(inputElement);
        return didChange;
    }

    function formatDecorationDimensionValue(value) {
        if (!Number.isFinite(value)) {
            return '';
        }

        if (Number.isInteger(value)) {
            return String(value);
        }

        return value.toFixed(2).replace(/\.0+$/, '').replace(/(\.\d*[1-9])0$/, '$1');
    }

    function limitDecorationDimensionInputPrecision(inputElement) {
        if (!(inputElement instanceof HTMLInputElement)) {
            return false;
        }

        const rawValue = inputElement.value;
        if (!rawValue || !rawValue.includes('.')) {
            return false;
        }

        const [integerPart, decimalPart = ''] = rawValue.split('.');
        if (decimalPart.length <= 2) {
            return false;
        }

        inputElement.value = `${integerPart}.${decimalPart.slice(0, 2)}`;
        return true;
    }

    function updateDecorationDimensionInputValidity(inputElement) {
        if (!(inputElement instanceof HTMLInputElement)) {
            return;
        }

        const rawValue = inputElement.value;
        const hasTooManyDecimals = rawValue.includes('.') && rawValue.split('.')[1].length > 2;

        if (hasTooManyDecimals) {
            inputElement.setCustomValidity('Use no more than 2 decimal places.');
            inputElement.setAttribute('aria-invalid', 'true');
            inputElement.title = 'Use no more than 2 decimal places.';
            return;
        }

        inputElement.setCustomValidity('');
        inputElement.removeAttribute('aria-invalid');
        inputElement.removeAttribute('title');
    }

    function updateDecorationDimensionValidationState() {
        const addToCartButton = document.getElementById('AddToCart');
        if (!addToCartButton) {
            return;
        }

        const dimensionInputs = Array.from(document.querySelectorAll('.product-decoration-dimensions-row-label-wrapper input'));
        const hasInvalidInputs = dimensionInputs.some((inputElement) => {
            if (!isDecorationDimensionInputActive(inputElement) || inputElement.value === '') {
                updateDecorationDimensionInputValidity(inputElement);
                return false;
            }

            updateDecorationDimensionInputValidity(inputElement);
            return !inputElement.validity.valid;
        });

        if (hasInvalidInputs) {
            addToCartButton.disabled = true;
            addToCartButton.setAttribute('data-decoration-dimensions-invalid', 'true');
            return;
        }

        addToCartButton.removeAttribute('data-decoration-dimensions-invalid');

        const variantScript = document.querySelector('variant-picker script[data-variant], script[data-variant]');
        if (!variantScript) {
            return;
        }

        try {
            const currentVariant = JSON.parse(variantScript.textContent);
            addToCartButton.disabled = !currentVariant || currentVariant.available === false;
        } catch (error) {
            console.error('Error parsing variant data for dimension validation:', error);
        }
    }

    // Price calculation functionality
    function initializePriceCalculation() {
        const widthInput = document.querySelector('[data-width-input]');
        const heightInput = document.querySelector('[data-height-input]');
        const dimensionInputs = document.querySelectorAll('.product-decoration-dimensions-row-label-wrapper input');
        const decorationMethodInputs = document.querySelectorAll('input[name="properties[Decoration Method]"]');

        if (widthInput && heightInput) {
            widthInput.addEventListener('input', updatePrice);
            heightInput.addEventListener('input', updatePrice);
        }

        dimensionInputs.forEach((inputElement) => {
            inputElement.addEventListener('input', () => {
                if (limitDecorationDimensionInputPrecision(inputElement)) {
                    updatePrice();
                }

                updateDecorationDimensionInputValidity(inputElement);
                updateDecorationAdditionalCosts();
                updateDecorationDimensionValidationState();
            });

            inputElement.addEventListener('change', () => {
                clampDecorationDimensionInputValue(inputElement);
                updateDecorationAdditionalCosts();
                updateDecorationDimensionValidationState();
            });

            inputElement.addEventListener('blur', () => {
                clampDecorationDimensionInputValue(inputElement);
                updateDecorationAdditionalCosts();
                updateDecorationDimensionValidationState();
            });
        });

        decorationMethodInputs.forEach((inputElement) => {
            inputElement.addEventListener('change', function() {
                applyDecorationDimensionMinimumDefaults();
                updateDecorationAdditionalCosts();
                updateDecorationDimensionValidationState();
            });
        });

        updateDecorationDimensionValidationState();
    }

    function initializeColorSelection() {
        const imprintColorButtons = document.querySelectorAll('[data-imprint-color]');

        if (imprintColorButtons.length > 0) {
            imprintColorButtons.forEach(button => {
                button.addEventListener('click', function () {
                    selectColorButton(this);
                });
            });

            // Also listen to radio input changes for ribbon colors
            const ribbonColorInputs = document.querySelectorAll('.imprint-color-input[name="properties[Ribbon Color]"]');
            ribbonColorInputs.forEach(input => {
                input.addEventListener('change', function () {
                    if (this.checked) {
                        const ribbonColorButton = this.closest('label[data-selected-color-name-ribbon]');
                        if (ribbonColorButton) {
                            selectColorButton(ribbonColorButton);
                        }
                    }
                });
            });

            // Initialize ribbon color display if a ribbon color is already selected
            const checkedRibbonColorInput = document.querySelector('.imprint-color-input[name="properties[Ribbon Color]"]:checked');
            if (checkedRibbonColorInput) {
                const ribbonColorButton = checkedRibbonColorInput.closest('label[data-selected-color-name-ribbon]');
                if (ribbonColorButton) {
                    const ribbonColorName = ribbonColorButton.getAttribute('data-selected-color-name-ribbon');
                    const selectedColorNameSpan = document.querySelector('span[data-selected-color-name-ribbon]');
                    if (selectedColorNameSpan && ribbonColorName) {
                        selectedColorNameSpan.textContent = ribbonColorName;
                    }
                }
            }
        }
    }

    function selectColorButton(button) {
        const colorName = button.getAttribute('data-imprint-color');
        const ribbonColorName = button.getAttribute('data-selected-color-name-ribbon');

        // Update ribbon color name display if this is a ribbon color button
        if (ribbonColorName) {
            const selectedColorNameSpan = document.querySelector('span[data-selected-color-name-ribbon]');
            if (selectedColorNameSpan) {
                selectedColorNameSpan.textContent = ribbonColorName;
            }
        }
    }

    function startInitialization() {
        const elements = getElements();

        if (!elements.productDecorationModal) {
            setTimeout(startInitialization, 100);
            return;
        }

        initializeProductDecoration();
    }

    startInitialization();

    function updateDecorationAdditionalCosts() {
        const additionalCostsElement = document.querySelector('[data-decoration-additional-costs]');
        const decorationDimensionsRow = document.querySelector('.product-decoration-dimensions-row');
        if (!additionalCostsElement) {
            return;
        }

        const selectedDecorationMethodInput = document.querySelector('input[name="properties[Decoration Method]"]:checked');
        if (!selectedDecorationMethodInput) {
            if (decorationDimensionsRow) {
                decorationDimensionsRow.style.display = 'none';
            }
            additionalCostsElement.style.display = 'none';
            additionalCostsElement.textContent = '';
            return;
        }

        const selectedMethod = selectedDecorationMethodInput.getAttribute('data-method') || selectedDecorationMethodInput.value;
        if (!selectedMethod || selectedMethod === 'Blank') {
            if (decorationDimensionsRow) {
                decorationDimensionsRow.style.display = 'none';
            }
            additionalCostsElement.style.display = 'none';
            additionalCostsElement.textContent = '';
            return;
        }

        if (decorationDimensionsRow) {
            decorationDimensionsRow.style.display = '';
        }

        const methodCardPrice = parseFloat(selectedDecorationMethodInput.getAttribute('data-method-card-price'));
        if (!Number.isFinite(methodCardPrice) || methodCardPrice <= 0) {
            additionalCostsElement.style.display = 'none';
            additionalCostsElement.textContent = '';
            return;
        }

        const widthInput = document.querySelector('[data-width-input], [data-width-input-color]');
        const heightInput = document.querySelector('[data-height-input], [data-height-input-color]');

        const width = parseFloat(widthInput?.value);
        const height = parseFloat(heightInput?.value);

        if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
            additionalCostsElement.style.display = 'none';
            additionalCostsElement.textContent = '';
            return;
        }

        const totalPrice = methodCardPrice * width * height;
        const totalPriceText = typeof formatMoney === 'function' ? formatMoney(Math.round(totalPrice * 100)) : totalPrice.toFixed(2);

        additionalCostsElement.textContent = `${totalPriceText} per item`;
        additionalCostsElement.style.display = '';
    }

    function updatePrice() {
        updateDecorationAdditionalCosts();

        if (document.querySelector('[data-decoration-method-radio]:checked')) {
            const widthInput = document.querySelector('[data-width-input]');
            const heightInput = document.querySelector('[data-height-input]');
            const priceDisplay = document.querySelector('[data-price-display]');
            const notesTextArea = document.querySelector('[data-notes-textarea]');
            const decorateMethodSection = document.querySelector('[data-decorate-method-section]');
            const setupChargeMessage = document.getElementById('setup-charge-message');
            let pricePerSquareInch = document.querySelector('[data-decoration-method-radio]:checked').getAttribute('data-method-card-price');
            let method = document.querySelector('[data-decoration-method-radio]:checked').getAttribute('data-method');

            if (!widthInput || !heightInput) return;

            const width = parseFloat(widthInput.value) || 0;
            const height = parseFloat(heightInput.value) || 0;

            const squareInches = width * height;

            let totalPrice = squareInches * pricePerSquareInch;

            if (isPersonalizationFeeEligible()) {
                decorateMethodSection.removeAttribute('hidden');
                if (setupChargeMessage) {
                    setupChargeMessage.style.display = 'block';
                }
            } else {
                if (setupChargeMessage) {
                    setupChargeMessage.style.display = 'none';
                }
                /*
                decorateMethodSection.setAttribute('hidden', 'true');
                widthInput.value = '';
                heightInput.value = '';
                notesTextArea.value = '';

                // Clear all design files
                allDesignFiles = [];
                const elements = getElements();
                elements.fileItems.innerHTML = '';
                elements.uploadArea.style.display = 'flex';
                elements.fileList.style.display = 'none';

                // Clear all CSV files
                allCsvFiles = [];
                removeCsvFile();

                // Clear file inputs from form
                const form = document.querySelector("[data-product-decoration-modal]");
                if (form) {
                    const fileInputs = form.querySelectorAll('[data-dynamic-file="true"]');
                    fileInputs.forEach(input => input.remove());
                }
                */
            }
        }
    }

    function updateMoreEmbellishmentsProperty() {
        const targetInput = document.querySelector('[data-more-embellishments-input]');
        if (!targetInput) {
            return;
        }

        const selectedOptions = Array.from(document.querySelectorAll('[data-more-embellishment-option]:checked'))
            .map((inputElement) => (inputElement.value || '').trim())
            .filter((value) => value.length > 0);

        if (selectedOptions.length === 0) {
            targetInput.value = '';
            targetInput.disabled = true;
            return;
        }

        targetInput.value = selectedOptions.join('; ');
        targetInput.disabled = false;
    }

    function initializeMoreEmbellishmentsProperty() {
        const optionInputs = document.querySelectorAll('[data-more-embellishment-option]');
        if (!optionInputs.length) {
            return;
        }

        optionInputs.forEach((inputElement) => {
            inputElement.addEventListener('change', updateMoreEmbellishmentsProperty);
        });

        updateMoreEmbellishmentsProperty();
    }

    function updateRibbonColorVisibility() {
        const ribbonColorHeader = document.querySelector('[data-ribbon-color-header]');
        const imprintColorWrapper = document.querySelector('[data-imprint-color-wrapper]');

        if (!ribbonColorHeader && !imprintColorWrapper) {
            return;
        }

        const selectedEmbellishmentInput = document.querySelector('input[name="properties[Embellishment]"]:checked');
        const shouldShowRibbonColors = Boolean(
            selectedEmbellishmentInput && selectedEmbellishmentInput.hasAttribute('data-ribbon-embellishment-option')
        );

        if (ribbonColorHeader) {
            ribbonColorHeader.hidden = !shouldShowRibbonColors;
        }

        if (imprintColorWrapper) {
            imprintColorWrapper.hidden = !shouldShowRibbonColors;
        }
    }

    function initializeRibbonColorVisibility() {
        const embellishmentInputs = document.querySelectorAll('input[name="properties[Embellishment]"]');
        if (!embellishmentInputs.length) {
            updateRibbonColorVisibility();
            return;
        }

        embellishmentInputs.forEach((inputElement) => {
            inputElement.addEventListener('change', updateRibbonColorVisibility);
        });

        updateRibbonColorVisibility();
    }

    function methodCardPriceCalculation() {
        const methodCardPrice = document.querySelectorAll('.decoration-method-card');
        methodCardPrice.forEach(card => {
            card.addEventListener('click', function() {
                setTimeout(() => {
                    updatePrice();
                }, 200);
            });
        });
    }

    // Initialize ribbon modal functionality
    if (document.querySelector('[data-ribbon-add]')) {
        initializeRibbonModal();
    }

    initializeRibbonColorVisibility();

    // Ribbon modal functions
    function initializeRibbonModal() {
        const ribbonModal = document.getElementById('product-ribbon-modal');
        if (!ribbonModal) return;

        const ribbonVariants = getRibbonVariants(ribbonModal);
        if (!ribbonVariants.length) return;

        initializeRibbonOptionSelection(ribbonModal, ribbonVariants);
        initializeAddRibbonButton(ribbonModal, ribbonVariants);
        checkExistingRibbonSelection(ribbonModal, ribbonVariants);
        syncRibbonPickerState(ribbonModal, ribbonVariants);
    }

    function getRibbonVariants(ribbonModal) {
        const ribbonVariantsScript = ribbonModal.querySelector('[data-ribbon-variants]');

        if (!ribbonVariantsScript) {
            return [];
        }

        try {
            const ribbonVariants = JSON.parse(ribbonVariantsScript.textContent);
            return Array.isArray(ribbonVariants) ? ribbonVariants : [];
        } catch (error) {
            console.error('Error parsing ribbon variants:', error);
            return [];
        }
    }

    function getRibbonOptionGroups(ribbonModal) {
        return Array.from(ribbonModal.querySelectorAll('[data-ribbon-option-group]')).sort((firstGroup, secondGroup) => {
            return parseInt(firstGroup.getAttribute('data-ribbon-option-position'), 10) - parseInt(secondGroup.getAttribute('data-ribbon-option-position'), 10);
        });
    }

    function getRibbonOptionInputs(ribbonModal) {
        return Array.from(ribbonModal.querySelectorAll('[data-ribbon-option-input]'));
    }

    function getRibbonSelectedValues(ribbonModal) {
        return getRibbonOptionGroups(ribbonModal).map((group) => {
            const checkedInput = group.querySelector('[data-ribbon-option-input]:checked');
            return checkedInput ? checkedInput.getAttribute('data-ribbon-option-value') || '' : '';
        });
    }

    function getRibbonSelectedOptions(ribbonModal) {
        return getRibbonOptionGroups(ribbonModal).map((group) => {
            const checkedInput = group.querySelector('[data-ribbon-option-input]:checked');

            if (!checkedInput) {
                return null;
            }

            return {
                name: group.getAttribute('data-ribbon-option-name') || '',
                value: checkedInput.getAttribute('data-ribbon-option-value') || ''
            };
        }).filter(Boolean);
    }

    function ribbonVariantMatchesSelections(variant, selectedValues) {
        return selectedValues.every((selectedValue, index) => {
            return !selectedValue || variant.options[index] === selectedValue;
        });
    }

    function findRibbonVariantBySelections(ribbonVariants, selectedValues) {
        if (!selectedValues.length) {
            return ribbonVariants.find((variant) => variant.available) || ribbonVariants[0] || null;
        }

        return ribbonVariants.find((variant) => {
            return variant.options.length === selectedValues.length && selectedValues.every((selectedValue, index) => variant.options[index] === selectedValue);
        }) || null;
    }

    function findFirstMatchingRibbonVariant(ribbonVariants, selectedValues, onlyAvailable = false) {
        return ribbonVariants.find((variant) => {
            if (onlyAvailable && !variant.available) {
                return false;
            }

            return ribbonVariantMatchesSelections(variant, selectedValues);
        }) || null;
    }

    function updateRibbonOptionLabels(ribbonModal) {
        getRibbonOptionGroups(ribbonModal).forEach((group) => {
            const checkedInput = group.querySelector('[data-ribbon-option-input]:checked');
            const selectedValueElement = group.querySelector('[data-ribbon-option-selected-value]');

            if (selectedValueElement) {
                selectedValueElement.textContent = checkedInput ? checkedInput.getAttribute('data-ribbon-option-value') || '' : '';
            }
        });
    }

    function updateRibbonPriceDisplay(ribbonModal, selectedVariant) {
        const ribbonPriceDisplay = ribbonModal.querySelector('[data-ribbon-price-display]');

        if (!ribbonPriceDisplay || !selectedVariant) {
            return;
        }

        if (typeof formatMoney === 'function') {
            ribbonPriceDisplay.textContent = formatMoney(selectedVariant.price);
            return;
        }

        ribbonPriceDisplay.textContent = `$${(selectedVariant.price / 100).toFixed(2)}`;
    }

    function updateRibbonAddButtonState(ribbonModal, selectedVariant) {
        const addRibbonButton = ribbonModal.querySelector('[data-add-ribbon-button]');

        if (!addRibbonButton) {
            return;
        }

        const isEnabled = Boolean(selectedVariant && selectedVariant.available);
        addRibbonButton.disabled = !isEnabled;
        addRibbonButton.setAttribute('aria-disabled', isEnabled ? 'false' : 'true');
    }

    function applyRibbonVariantSelection(ribbonModal, selectedVariant) {
        if (!selectedVariant) {
            return;
        }

        selectedVariant.options.forEach((optionValue, index) => {
            const matchingInput = getRibbonOptionInputs(ribbonModal).find((input) => {
                return parseInt(input.getAttribute('data-option-position'), 10) === index + 1 && (input.getAttribute('data-ribbon-option-value') || '') === optionValue;
            });

            if (matchingInput) {
                matchingInput.checked = true;
            }
        });

        ribbonModal.setAttribute('data-ribbon-selected-variant-id', String(selectedVariant.id));
        updateRibbonOptionLabels(ribbonModal);
        updateRibbonPriceDisplay(ribbonModal, selectedVariant);
        updateRibbonAddButtonState(ribbonModal, selectedVariant);
    }

    function updateRibbonOptionAvailability(ribbonModal, ribbonVariants) {
        const selectedValues = getRibbonSelectedValues(ribbonModal);

        getRibbonOptionInputs(ribbonModal).forEach((input) => {
            const optionPosition = parseInt(input.getAttribute('data-option-position'), 10) - 1;
            const nextSelectedValues = [...selectedValues];
            nextSelectedValues[optionPosition] = input.getAttribute('data-ribbon-option-value') || '';

            const hasAvailableVariant = Boolean(findFirstMatchingRibbonVariant(ribbonVariants, nextSelectedValues, true));
            const valueWrapper = input.closest('[data-ribbon-option-value-wrapper]');

            input.disabled = !hasAvailableVariant;

            if (valueWrapper) {
                valueWrapper.classList.toggle('is-disabled', !hasAvailableVariant);
            }
        });
    }

    function syncRibbonPickerState(ribbonModal, ribbonVariants) {
        const selectedValues = getRibbonSelectedValues(ribbonModal);
        let selectedVariant = findRibbonVariantBySelections(ribbonVariants, selectedValues);

        if (!selectedVariant || !selectedVariant.available) {
            selectedVariant = findFirstMatchingRibbonVariant(ribbonVariants, selectedValues, true);
        }

        if (!selectedVariant) {
            selectedVariant = ribbonVariants.find((variant) => variant.available) || ribbonVariants[0] || null;
        }

        if (!selectedVariant) {
            updateRibbonAddButtonState(ribbonModal, null);
            return null;
        }

        applyRibbonVariantSelection(ribbonModal, selectedVariant);
        updateRibbonOptionAvailability(ribbonModal, ribbonVariants);
        return selectedVariant;
    }

    function initializeRibbonOptionSelection(ribbonModal, ribbonVariants) {
        getRibbonOptionInputs(ribbonModal).forEach((input) => {
            input.addEventListener('change', function() {
                syncRibbonPickerState(ribbonModal, ribbonVariants);
            });
        });
    }

    function initializeAddRibbonButton(ribbonModal, ribbonVariants) {
        const addRibbonButton = ribbonModal.querySelector('[data-add-ribbon-button]');

        if (addRibbonButton) {
            addRibbonButton.addEventListener('click', function() {
                const selectedVariant = syncRibbonPickerState(ribbonModal, ribbonVariants);

                if (!selectedVariant || !selectedVariant.available) {
                    alert('Please select an available ribbon option combination');
                    return;
                }

                const selectedOptions = getRibbonSelectedOptions(ribbonModal);
                const selectedColor = selectedOptions.find((option) => option.name.toLowerCase() === 'color');
                const ribbonPriceDisplay = ribbonModal.querySelector('[data-ribbon-price-display]');
                const ribbonData = {
                    color: selectedColor ? selectedColor.value : '',
                    optionSelections: selectedOptions,
                    optionValues: selectedOptions.map((option) => option.value),
                    price: ribbonPriceDisplay ? ribbonPriceDisplay.textContent.trim() : '',
                    productHandle: getProductHandleFromPage(),
                    variantId: String(selectedVariant.id)
                };

                localStorage.setItem('ribbonSelection', JSON.stringify(ribbonData));

                ribbonModal.removeAttribute('open');
                updateRibbonDisplay(ribbonData);
            });
        }
    }

    function getRibbonSummaryText(ribbonData) {
        if (Array.isArray(ribbonData.optionSelections) && ribbonData.optionSelections.length) {
            return ribbonData.optionSelections.map((option) => `${option.name}: ${option.value}`).join(' / ');
        }

        if (ribbonData.color) {
            return `Color: ${ribbonData.color}`;
        }

        return '';
    }

    function updateRibbonDisplay(ribbonData) {
        const originalBlock = document.querySelector('.product-decoration-wrapper-ribbon');
        const summaryBlock = document.querySelector('.product-ribbon-summary');

        if (originalBlock && summaryBlock) {
            originalBlock.style.display = 'none';
            summaryBlock.style.display = 'block';

            const ribbonDetails = summaryBlock.querySelector('[data-ribbon-selected-options]');

            if (ribbonDetails) {
                ribbonDetails.textContent = getRibbonSummaryText(ribbonData);
            }

            // Initialize remove and edit buttons
            initializeRibbonSummaryButtons();
        }
    }

    function initializeRibbonSummaryButtons() {
        const removeBtn = document.querySelector('[data-remove-ribbon-btn]');

        if (removeBtn) {
            removeBtn.addEventListener('click', function() {
                localStorage.removeItem('ribbonSelection');
                location.reload();
            });
        }
    }

    function applyRibbonSelectionToModal(ribbonModal, ribbonVariants, ribbonData) {
        if (Array.isArray(ribbonData.optionValues) && ribbonData.optionValues.length) {
            ribbonData.optionValues.forEach((optionValue, index) => {
                const matchingInput = getRibbonOptionInputs(ribbonModal).find((input) => {
                    return parseInt(input.getAttribute('data-option-position'), 10) === index + 1 && (input.getAttribute('data-ribbon-option-value') || '') === optionValue;
                });

                if (matchingInput) {
                    matchingInput.checked = true;
                }
            });

            syncRibbonPickerState(ribbonModal, ribbonVariants);
            return;
        }

        if (ribbonData.variantId) {
            const matchingVariant = ribbonVariants.find((variant) => String(variant.id) === String(ribbonData.variantId));

            if (matchingVariant) {
                applyRibbonVariantSelection(ribbonModal, matchingVariant);
                updateRibbonOptionAvailability(ribbonModal, ribbonVariants);
            }
        }
    }

    function checkExistingRibbonSelection(ribbonModal, ribbonVariants) {
        const savedRibbon = localStorage.getItem('ribbonSelection');

        if (savedRibbon) {
            try {
                const ribbonData = JSON.parse(savedRibbon);
                const currentProductHandle = getProductHandleFromPage();

                // Only restore if the saved product handle matches the current page product handle
                if (ribbonData.productHandle && currentProductHandle && ribbonData.productHandle === currentProductHandle) {
                    applyRibbonSelectionToModal(ribbonModal, ribbonVariants, ribbonData);
                    updateRibbonDisplay(ribbonData);
                } else {
                    localStorage.removeItem('ribbonSelection');
                }
            } catch (error) {
                console.error('Error parsing ribbon data:', error);
                localStorage.removeItem('ribbonSelection');
            }
        }
    }

    // Printing options functionality
    function initializePrintingOptions() {
        const printingOptionsSection = document.querySelector('[data-printing-options-section]');

        if (!printingOptionsSection) {
            return;
        }

        const uvPrintingToggle = document.querySelector('[data-uv-printing-toggle]');
        const printingOptionsContent = document.querySelector('[data-printing-options-content]');
        const printingFileInput = document.querySelector('[data-printing-file-input]');
        const printingFileUploadTrigger = document.querySelector('[data-printing-file-upload-trigger]');

        // UV Printing toggle functionality
        if (uvPrintingToggle && printingOptionsContent) {
            uvPrintingToggle.addEventListener('change', function() {
                if (this.checked) {
                    printingOptionsContent.style.display = 'block';
                } else {
                    printingOptionsContent.style.display = 'none';
                    resetPrintingOptions();
                }
            });
        }

        if (printingFileInput && printingFileUploadTrigger) {
            printingFileUploadTrigger.addEventListener('click', function(e) {
                e.preventDefault();
                printingFileInput.click();
            });

            printingFileInput.addEventListener('change', function(e) {
                const files = Array.from(e.target.files);
                if (files.length > 0) {
                    handlePrintingFileUpload(files);
                }
            });
        }
    }

    // Pictogram functionality
    function initializePictogramOptions() {
        const pictogramSection = document.querySelector('[data-pictogram-section]');

        if (!pictogramSection) {
            return;
        }

        const customPictogramToggle = document.querySelector('[data-custom-pictogram-toggle]');
        const pictogramUploadContent = document.querySelector('[data-pictogram-upload-content]');
        const pictogramSelectRow = document.querySelector('[data-pictogram-select-row]');
        const pictogramFileInput = document.querySelector('[data-pictogram-file-input]');
        const pictogramFileUploadTrigger = document.querySelector('[data-pictogram-file-upload-trigger]');

        initializePictogramDropdown();

        // Custom Pictogram toggle functionality
        if (customPictogramToggle && pictogramUploadContent && pictogramSelectRow) {
            customPictogramToggle.addEventListener('change', function() {
                if (this.checked) {
                    pictogramUploadContent.style.display = 'block';
                    pictogramSelectRow.style.display = 'none';

                    removePictogramOverlay();
                } else {
                    pictogramUploadContent.style.display = 'none';
                    pictogramSelectRow.style.display = 'block';
                    resetPictogramOptions();
                }
            });
        }

        if (pictogramFileInput && pictogramFileUploadTrigger) {
            pictogramFileUploadTrigger.addEventListener('click', function(e) {
                e.preventDefault();
                pictogramFileInput.click();
            });

            pictogramFileInput.addEventListener('change', function(e) {
                const files = Array.from(e.target.files);
                if (files.length > 0) {
                    handlePictogramFileUpload(files);
                }
            });
        }
    }

    // Initialize pictogram custom dropdown
    function initializePictogramDropdown() {
        const dropdown = document.querySelector('[data-pictogram-dropdown]');
        if (!dropdown) return;

        const trigger = dropdown.querySelector('[data-pictogram-trigger]');
        const menu = dropdown.querySelector('[data-pictogram-menu]');
        const options = dropdown.querySelectorAll('[data-pictogram-option]');
        const selectedText = dropdown.querySelector('[data-pictogram-selected-text]');

        if (!trigger || !menu || !selectedText) return;

        // Toggle dropdown
        trigger.addEventListener('click', function(e) {
            e.stopPropagation();
            dropdown.classList.toggle('is-open');
        });

        // Handle option selection
        options.forEach(option => {
            option.addEventListener('click', function() {
                const radioInput = this.querySelector('input[type="radio"]');
                const label = this.getAttribute('data-pictogram-option');

                if (radioInput) {
                    radioInput.checked = true;
                    selectedText.textContent = label;

                    // Remove selected class from all options
                    options.forEach(opt => opt.classList.remove('selected'));
                    // Add selected class to current option
                    this.classList.add('selected');

                    // Close dropdown
                    dropdown.classList.remove('is-open');
                }
            });
        });

        // Close dropdown when clicking outside
        document.addEventListener('click', function(e) {
            if (!dropdown.contains(e.target)) {
                dropdown.classList.remove('is-open');
            }
        });
    }

    function resetPictogramOptions() {
        const pictogramFileInput = document.querySelector('[data-pictogram-file-input]');
        const pictogramFileDisplay = document.querySelector('[data-pictogram-file-display]');
        const pictogramFileUploadSection = document.querySelector('[data-pictogram-file-upload-section]');
        const pictogramUploadAnother = document.querySelector('[data-pictogram-upload-another]');
        const selectedText = document.querySelector('[data-pictogram-selected-text]');
        const radioInputs = document.querySelectorAll('input[name="properties[Pictogram]"]');
        const options = document.querySelectorAll('[data-pictogram-option]');

        pictogramFiles = [];

        if (pictogramFileInput) {
            pictogramFileInput.value = '';
        }

        if (pictogramFileDisplay) {
            pictogramFileDisplay.style.display = 'none';
        }

        if (pictogramFileUploadSection) {
            pictogramFileUploadSection.style.display = 'flex';
        }

        if (pictogramUploadAnother) {
            pictogramUploadAnother.style.display = 'none';
        }

        // Remove dynamic file inputs for pictogram
        const form = document.querySelector("[data-product-decoration-modal]");
        if (form) {
            const pictogramDynamicInputs = form.querySelectorAll('[data-dynamic-file="true"][data-file-type="pictogram"]');
            pictogramDynamicInputs.forEach(input => input.remove());
        }

        // Reset custom dropdown
        if (selectedText) {
            const placeholderText = selectedText.getAttribute('data-placeholder') || 'Select a pictogram';
            selectedText.textContent = placeholderText;
        }

        // Uncheck all radio inputs
        radioInputs.forEach(radio => {
            radio.checked = false;
        });

        // Remove selected class from all options
        options.forEach(opt => {
            opt.classList.remove('selected');
        });
    }

    function handlePictogramFileUpload(files) {
        const validFiles = [];
        const invalidFiles = [];

        files.forEach(file => {
            const validation = validatePictogramFile(file);
            if (validation.isValid) {
                validFiles.push(file);
            } else {
                invalidFiles.push(validation.message);
            }
        });

        if (invalidFiles.length > 0) {
            invalidFiles.forEach(message => {
                alert(message);
            });
        }

        if (validFiles.length > 0) {
            displayPictogramFiles(validFiles);
        }
    }

    function validatePictogramFile(file) {
        const allowedTypes = [
            'application/postscript', // .ai
            'application/x-illustrator', // .ai
            'application/coreldraw', // .cdr
            'application/x-coreldraw', // .cdr
            'application/postscript', // .eps
            'application/pdf', // .pdf
            'image/png', // .png
            'image/jpeg', // .jpg, .jpeg
            'image/jpg' // .jpg
        ];

        const allowedExtensions = ['.ai', '.cdr', '.eps', '.pdf', '.png', '.jpg', '.jpeg'];

        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

        if (!allowedExtensions.includes(fileExtension)) {
            return {
                isValid: false,
                message: `File "${file.name}" has an unsupported format. Supported formats: AI, CDR, EPS, PDF, PNG, JPG, JPEG.`
            };
        }

        return { isValid: true };
    }

    // Store pictogram files globally
    let pictogramFiles = [];
    
    // Store printing files globally
    let printingFiles = [];

    function displayPictogramFiles(files) {
        const pictogramFileDisplay = document.querySelector('[data-pictogram-file-display]');
        const pictogramFileItem = document.querySelector('[data-pictogram-file-item]');
        const pictogramFileUploadSection = document.querySelector('[data-pictogram-file-upload-section]');
        const pictogramUploadAnother = document.querySelector('[data-pictogram-upload-another]');

        if (pictogramFileDisplay && pictogramFileItem) {
            pictogramFiles = [...pictogramFiles, ...files];

            createMultipleFileInputs(pictogramFiles, 'pictogram');

            pictogramFileItem.innerHTML = '';

            pictogramFiles.forEach((file, index) => {
                const fileWrapper = document.createElement('div');
                fileWrapper.className = 'pictogram-file-item-wrapper';
                fileWrapper.setAttribute('data-file-index', index);
                fileWrapper.innerHTML = `
                   <div class="printing-file-item-wrapper">
                     <div class="printing-file-item">
                        <div class="printing-file-item-content">
                            <div class="printing-file-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M16.6143 7.23366C16.606 7.20699 16.5968 7.18088 16.5868 7.15533C16.5473 7.06491 16.492 6.98223 16.4235 6.91116L11.4235 1.91116C11.3524 1.84269 11.2697 1.78738 11.1793 1.74783C11.1543 1.73616 11.1277 1.72949 11.101 1.72033C11.0312 1.69668 10.9587 1.68239 10.8852 1.67783C10.8668 1.67616 10.851 1.66699 10.8335 1.66699H5.00016C4.081 1.66699 3.3335 2.41449 3.3335 3.33366V16.667C3.3335 17.5862 4.081 18.3337 5.00016 18.3337H15.0002C15.9193 18.3337 16.6668 17.5862 16.6668 16.667V7.50033C16.6668 7.48283 16.6577 7.46699 16.656 7.44866C16.6516 7.37547 16.6376 7.30318 16.6143 7.23366ZM13.8218 6.66699H11.6668V4.51199L13.8218 6.66699ZM5.00016 16.667V3.33366H10.0002V7.50033C10.0002 7.72134 10.088 7.9333 10.2442 8.08958C10.4005 8.24586 10.6125 8.33366 10.8335 8.33366H15.0002L15.0018 16.667H5.00016Z" fill="black" fill-opacity="0.7"/>
                                </svg>
                            </div>
                            <span class="printing-file-name">${file.name}</span>
                        </div>
                    </div>
                   </div>
                    <button type="button" class="printing-file-delete" onclick="removePictogramFile(this, ${index})">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M4.16667 16.667C4.16667 17.109 4.34226 17.5329 4.65482 17.8455C4.96738 18.1581 5.39131 18.3337 5.83333 18.3337H14.1667C14.6087 18.3337 15.0326 18.1581 15.3452 17.8455C15.6577 17.5329 15.8333 17.109 15.8333 16.667V6.66699H17.5V5.00033H14.1667V3.33366C14.1667 2.89163 13.9911 2.46771 13.6785 2.15515C13.3659 1.84259 12.942 1.66699 12.5 1.66699H7.5C7.05797 1.66699 6.63405 1.84259 6.32149 2.15515C6.00893 2.46771 5.83333 2.89163 5.83333 3.33366V5.00033H2.5V6.66699H4.16667V16.667ZM7.5 3.33366H12.5V5.00033H7.5V3.33366ZM6.66667 6.66699H14.1667V16.667H5.83333V6.66699H6.66667Z" fill="#232323"/>
                            <path d="M7.5 8.33301H9.16667V14.9997H7.5V8.33301ZM10.8333 8.33301H12.5V14.9997H10.8333V8.33301Z" fill="#232323"/>
                        </svg>
                    </button>
                `;
                pictogramFileItem.appendChild(fileWrapper);
            });

            pictogramFileDisplay.style.display = 'block';

            // Hide the initial upload section and show "upload another file" option
            if (pictogramFileUploadSection) {
                pictogramFileUploadSection.style.display = 'none';
            }

            if (pictogramUploadAnother) {
                pictogramUploadAnother.style.display = 'flex';
            }
        }
    }

    function removePictogramFile(button, fileIndex) {
        pictogramFiles.splice(fileIndex, 1);

        const pictogramFileInput = document.querySelector('[data-pictogram-file-input]');
        const pictogramFileDisplay = document.querySelector('[data-pictogram-file-display]');
        const pictogramFileUploadSection = document.querySelector('[data-pictogram-file-upload-section]');
        const pictogramUploadAnother = document.querySelector('[data-pictogram-upload-another]');

        if (pictogramFileInput) {
            pictogramFileInput.value = '';
        }

        createMultipleFileInputs(pictogramFiles, 'pictogram');

        // If no files left, hide display and show initial upload section
        if (pictogramFiles.length === 0) {
            if (pictogramFileDisplay) {
                pictogramFileDisplay.style.display = 'none';
            }
            if (pictogramFileUploadSection) {
                pictogramFileUploadSection.style.display = 'flex';
            }
            if (pictogramUploadAnother) {
                pictogramUploadAnother.style.display = 'none';
            }
        } else {
            // Rebuild the display with remaining files
            displayPictogramFiles([]);
        }
    }

    window.removePictogramFile = removePictogramFile;

    function handlePrintingFileUpload(files) {
        const validFiles = [];
        const invalidFiles = [];

        files.forEach(file => {
            const validation = validatePrintingFile(file);
            if (validation.isValid) {
                validFiles.push(file);
            } else {
                invalidFiles.push(validation.message);
            }
        });

        if (invalidFiles.length > 0) {
            invalidFiles.forEach(message => {
                alert(message);
            });
        }

        if (validFiles.length > 0) {
            printingFiles = [...printingFiles, ...validFiles];
            displayPrintingFiles();
        }

        const printingFileInput = document.querySelector('[data-printing-file-input]');
        if (printingFileInput) {
            printingFileInput.value = '';
        }
    }

    // Display printing files
    function displayPrintingFiles() {
        const fileDisplay = document.querySelector('[data-printing-file-display]');
        const fileItem = document.querySelector('[data-printing-file-item]');
        const fileUploadSection = document.querySelector('[data-file-upload-section]');

        if (fileDisplay && fileItem) {
            createMultipleFileInputs(printingFiles, 'printing');
            
            fileItem.innerHTML = '';
            
            printingFiles.forEach((file, index) => {
                const fileWrapper = document.createElement('div');
                fileWrapper.className = 'printing-file-item-wrapper';
                fileWrapper.setAttribute('data-file-index', index);
                fileWrapper.innerHTML = createPrintingFileItemHTML(file, index);
                fileItem.appendChild(fileWrapper);
            });

            fileDisplay.style.display = 'block';

            if (fileUploadSection) {
                fileUploadSection.style.display = 'none';
            }

            // Add delete event listeners
            const deleteButtons = fileItem.querySelectorAll('.printing-file-delete');
            deleteButtons.forEach(btn => {
                btn.addEventListener('click', function() {
                    const index = parseInt(this.getAttribute('data-index'));
                    removePrintingFile(index);
                });
            });
        }
    }

    function createPrintingFileItemHTML(file, index) {
        return `
            <div class="printing-file-item-content">
                <span class="printing-file-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M16.6143 7.23366C16.606 7.20699 16.5968 7.18088 16.5868 7.15533C16.5473 7.06491 16.492 6.98223 16.4235 6.91116L11.4235 1.91116C11.3524 1.84269 11.2697 1.38738 11.1793 1.74783C11.1543 1.73616 11.1277 1.72949 11.101 1.72033C11.0312 1.69668 10.9587 1.68239 10.8852 1.67783C10.8668 1.67616 10.851 1.66699 10.8335 1.66699H5.00016C4.081 1.66699 3.3335 2.41449 3.3335 3.33366V16.667C3.3335 17.5862 4.081 18.3337 5.00016 18.3337H15.0002C15.9193 18.3337 16.6668 17.5862 16.6668 16.667V7.50033C16.6668 7.48283 16.6577 7.46699 16.656 7.44866C16.6516 7.37547 16.6376 7.30318 16.6143 7.23366ZM13.8218 6.66699H11.6668V4.51199L13.8218 6.66699ZM5.00016 16.667V3.33366H10.0002V7.50033C10.0002 7.72134 10.088 7.9333 10.2442 8.08958C10.4005 8.24586 10.6125 8.33366 10.8335 8.33366H15.0002L15.0018 16.667H5.00016Z" fill="black" fill-opacity="0.7"/>
                    </svg>
                </span>
                <span class="printing-file-name">${file.name}</span>
            </div>
            <button type="button" class="printing-file-delete" data-index="${index}">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M4.16667 16.667C4.16667 17.109 4.34226 17.5329 4.65482 17.8455C4.96738 18.1581 5.39131 18.3337 5.83333 18.3337H14.1667C14.6087 18.3337 15.0326 18.1581 15.3452 17.8455C15.6577 17.5329 15.8333 17.109 15.8333 16.667V6.66699H17.5V5.00033H14.1667V3.33366C14.1667 2.89163 13.9911 2.46771 13.6785 2.15515C13.3659 1.84259 12.942 1.66699 12.5 1.66699H7.5C7.05797 1.66699 6.63405 1.84259 6.32149 2.15515C6.00893 2.46771 5.83333 2.89163 5.83333 3.33366V5.00033H2.5V6.66699H4.16667V16.667ZM7.5 3.33366H12.5V5.00033H7.5V3.33366ZM6.66667 6.66699H14.1667V16.667H5.83333V6.66699H6.66667Z" fill="#232323"/>
                    <path d="M7.5 8.33301H9.16667V14.9997H7.5V8.33301ZM10.8333 8.33301H12.5V14.9997H10.8333V8.33301Z" fill="#232323"/>
                </svg>
            </button>
        `;
    }

    function removePrintingFile(fileIndex) {
        printingFiles.splice(fileIndex, 1);

        const printingFileInput = document.querySelector('[data-printing-file-input]');
        const fileDisplay = document.querySelector('[data-printing-file-display]');
        const fileUploadSection = document.querySelector('[data-file-upload-section]');

        if (printingFileInput) {
            printingFileInput.value = '';
        }

        createMultipleFileInputs(printingFiles, 'printing');

        if (printingFiles.length === 0) {
            if (fileDisplay) {
                fileDisplay.style.display = 'none';
            }
            if (fileUploadSection) {
                fileUploadSection.style.display = 'flex';
            }
        } else {
            displayPrintingFiles();
        }
    }

    function validatePrintingFile(file) {
        const allowedExtensions = ['.ai', '.cdr', '.eps', '.pdf'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

        if (!allowedExtensions.includes(fileExtension)) {
            return {
                isValid: false,
                message: `File "${file.name}" is not a supported format. Please upload AI, CDR, EPS, or PDF files only.`
            };
        }

        // Check file size (max 20MB)
        const maxSizeMB = 20;
        const maxSizeBytes = maxSizeMB * 1024 * 1024;
        if (file.size > maxSizeBytes) {
            return {
                isValid: false,
                message: `File "${file.name}" is too large. Maximum file size is ${maxSizeMB}MB.`
            };
        }

        return { isValid: true };
    }

    // Reset printing options when toggle is turned off
    function resetPrintingOptions() {
        const faceRadioButtons = document.querySelectorAll('input[name="properties[Face Selection]"]');
        faceRadioButtons.forEach(radio => {
            radio.checked = false;
        });

        const dimensionInputs = document.querySelectorAll('[data-printing-width-1], [data-printing-height]');
        dimensionInputs.forEach(input => {
            input.value = '0';
        });

        const printingFileInput = document.querySelector('[data-printing-file-input]');
        const fileDisplay = document.querySelector('[data-printing-file-display]');
        const fileUploadSection = document.querySelector('[data-file-upload-section]');

        printingFiles = [];

        if (printingFileInput) {
            printingFileInput.value = '';
        }

        if (fileDisplay) {
            fileDisplay.style.display = 'none';
        }

        if (fileUploadSection) {
            fileUploadSection.style.display = 'flex';
        }

        // Remove dynamic file inputs for printing
        const form = document.querySelector("[data-product-decoration-modal]");
        if (form) {
            const printingDynamicInputs = form.querySelectorAll('[data-dynamic-file="true"][data-file-type="printing"]');
            printingDynamicInputs.forEach(input => input.remove());
        }
    }
    function hideDecorationInfos(variant) {
        const decorationInfosContainer = document.querySelector('[data-product-decoration-modal]');
        const variantsMetafieldsScript = document.querySelector('script[data-variants-metafields]');
        if (!variantsMetafieldsScript) {
            return;
        }

        let variantsMetafields;
        try {
            variantsMetafields = JSON.parse(variantsMetafieldsScript.textContent);
        } catch (error) {
            console.error('Error parsing variants metafields data:', error);
            return;
        }

        if (!variantsMetafields[variant]) {
            return;
        }

        const metafields = variantsMetafields[variant.id];

        /*

        const showDecorationInfos = metafields.show_decoration_infos;

        if (showDecorationInfos === 'Yes') {
            decorationInfosContainer.style.display = 'block';
        } else {
            decorationInfosContainer.style.display = 'none';
            // Function to clear all properties inputs
            clearPropertiesInputs();
        }
            */
    }

    function clearPropertiesInputs() {
        setTimeout(() => {
            const decorationInfosContainer = document.querySelectorAll('[data-product-decoration-modal] input:not([data-decoration-method-radio]), [data-product-decoration-modal] textarea');

            if (document.querySelector('[data-file-list]')) {
                const dataUploadArea = document.querySelector('[data-upload-area]');
                const fileList = document.querySelector('[data-file-list]');
                const addNoteTrigger = document.querySelector('[data-add-note-trigger]');
                const notesSection = document.querySelector('[data-notes-section]');

                dataUploadArea.style.display = 'flex';
                fileList.style.display = 'none';
                addNoteTrigger.setAttribute('important-hidden', 'true');
                elements.dataUploadInstructions.textContent = 'Art Not Ready? You are welcome to place your order now, even without your design file. Your artwork can be submitted after checkout, at your convenience.';
                notesSection.setAttribute('hidden', 'true');

                allDesignFiles = [];
                const elements = getElements();
                elements.fileItems.innerHTML = '';
                elements.uploadArea.style.display = 'flex';
                elements.fileList.style.display = 'none';
            }

            decorationInfosContainer.forEach(input => {
                input.value = '';
                input.checked = false;
            });

            // Clear all CSV files
            allCsvFiles = [];
            removeCsvFile();
        }, 300);
    }

    document.addEventListener('variant:changed', function (event) {
        let variant = event.detail.variant;
        hideDecorationInfos(variant);
    });
})

function getProductHandleFromPage() {
    const productHandleElement = document.querySelector('[data-product-handle]');
    if (productHandleElement) {
        return productHandleElement.getAttribute('data-product-handle');
    }
    return null;
}



// ------------------------------------------------------------------------------------------------------------
// QUANTITY DISCOUNTS TABLE UPDATE ON VARIANT CHANGE
// ------------------------------------------------------------------------------------------------------------

document.addEventListener('variant:changed', function (event) {
    let variant = event.detail.variant;

    updateDecorationInputLimits(variant);
    updateTemplateLink(variant);
    updateQuantityDiscountsTable(variant);
    updateProductMetaMinimumBespokePriceDisplay(variant);
});

function updateQuantityDiscountsTable(variant) {
    const quantityDiscountsWrapper = document.querySelector('.quantity-discounts-wrapper');

    if (!quantityDiscountsWrapper || !variant) {
        return;
    }

    try {
        const variantDataSpan = quantityDiscountsWrapper.querySelector(`[data-variant-id="${variant.id}"]`);

        if (!variantDataSpan) {
            quantityDiscountsWrapper.style.display = 'none';
            // Hide price label and container when table is hidden
            const priceLabelElement = document.querySelector('[data-table-price-label]');
            const priceContainerElement = document.querySelector('[data-table-price]');
            if (priceLabelElement) {
                priceLabelElement.style.display = 'none';
            }
            if (priceContainerElement) {
                priceContainerElement.style.display = 'none';
            }
            return;
        }

        // Get the variant data from the span
        const priceBreaksJson = variantDataSpan.getAttribute('data-price-breaks');

        if (!priceBreaksJson) {
            quantityDiscountsWrapper.style.display = 'none';
            // Hide price label and container when table is hidden
            const priceLabelElement = document.querySelector('[data-table-price-label]');
            const priceContainerElement = document.querySelector('[data-table-price]');
            if (priceLabelElement) {
                priceLabelElement.style.display = 'none';
            }
            if (priceContainerElement) {
                priceContainerElement.style.display = 'none';
            }
            return;
        }

        // Parse the price breaks JSON data
        const priceBreaks = JSON.parse(priceBreaksJson);

        if (!Array.isArray(priceBreaks) || priceBreaks.length === 0) {
            quantityDiscountsWrapper.style.display = 'none';
            // Hide price label and container when table is hidden
            const priceLabelElement = document.querySelector('[data-table-price-label]');
            const priceContainerElement = document.querySelector('[data-table-price]');
            if (priceLabelElement) {
                priceLabelElement.style.display = 'none';
            }
            if (priceContainerElement) {
                priceContainerElement.style.display = 'none';
            }
            return;
        }

        // Show the table
        quantityDiscountsWrapper.style.display = 'block';

        // Get the table element
        const table = quantityDiscountsWrapper.querySelector('table');
        if (!table) return;

        // Clear existing table body
        const tbody = table.querySelector('tbody');
        if (tbody) {
            tbody.innerHTML = '';
        }

        // Update header row with quantities
        updateTableHeader(table, priceBreaks);

        // Calculate additional price from decoration selections
        const displayAdditionalPrice = calculateDecorationAdditionalPrice();
        const retailAdditionalPrice = calculateDecorationAdditionalPrice('retail');
        const displayPerUnitAdditionalPrice = displayAdditionalPrice?.perUnitPriceCents || 0;
        const retailPerUnitAdditionalPrice = retailAdditionalPrice?.perUnitPriceCents || 0;
        const sizeMultiplier = getCustomSizingMultiplier();

        // Create and append table rows based on login status
        const isCustomerLoggedIn = window.themeVariables && window.themeVariables.settings.isCustomerLoggedIn;
        const isDistributor = Boolean(window.themeVariables?.settings?.isDistributor);

        if (isCustomerLoggedIn) {
            // For logged-in users: Distributor gets Distributor Price, non-Distributor gets Retail Price
            createRetailPriceRow(tbody, priceBreaks, retailPerUnitAdditionalPrice, sizeMultiplier, variant.id);
            if (isDistributor) {
                createDistributorPriceRow(tbody, priceBreaks, displayPerUnitAdditionalPrice, sizeMultiplier, variant.id);
            }
            createSavingsPercentageRow(tbody, priceBreaks, sizeMultiplier, variant.id);
        } else {
            // For non-logged-in users: Price, Savings %
            createPriceRow(tbody, priceBreaks, retailPerUnitAdditionalPrice, sizeMultiplier, variant.id);
            createSavingsPercentageRow(tbody, priceBreaks, sizeMultiplier, variant.id);
        }

        // Get current quantity
        const quantity = getCurrentProductQuantity();

        // Update the single unit price display (with quantity multiplication)
        updateTablePriceValue(priceBreaks, displayAdditionalPrice, quantity, variant.id);

    } catch (error) {
        quantityDiscountsWrapper.style.display = 'none';
    }
}

function updateTableHeader(table, priceBreaks) {
    const thead = table.querySelector('thead tr');
    if (!thead) return;

    const firstTh = thead.querySelector('th:first-child');
    thead.innerHTML = '';
    thead.appendChild(firstTh);

    priceBreaks.forEach(breakItem => {
        const th = document.createElement('th');
        th.textContent = breakItem.qty;
        thead.appendChild(th);
    });
}

function createPriceRow(tbody, priceBreaks, additionalPrice = 0, sizeMultiplier = 1, variantId = null) {
    // For non-logged-in users: show regular prices
    const priceRow = document.createElement('tr');
    const priceLabelTd = document.createElement('td');
    priceLabelTd.textContent = getTranslatedText('general.quantity_discounts.price') || 'Price';
    priceRow.appendChild(priceLabelTd);

    priceBreaks.forEach(breakItem => {
        const td = document.createElement('td');
        const breakPrice = parseFloat(breakItem.prc) * 100;
        const adjustedBasePrice = getCartTransformUnitBasePriceCents(breakPrice, breakItem.qty, variantId, {
            sizeMultiplier
        });
        const finalPrice = adjustedBasePrice + additionalPrice;
        td.textContent = formatMoney(finalPrice);
        priceRow.appendChild(td);
    });

    tbody.appendChild(priceRow);
}

function createRetailPriceRow(tbody, priceBreaks, additionalPrice = 0, sizeMultiplier = 1, variantId = null) {
    // For logged-in users: show retail prices (from price_breaks data)
    const retailRow = document.createElement('tr');
    const retailLabelTd = document.createElement('td');
    retailLabelTd.textContent = getTranslatedText('general.quantity_discounts.retail_price') || 'Retail Price';
    retailRow.appendChild(retailLabelTd);

    priceBreaks.forEach(breakItem => {
        const td = document.createElement('td');
        const breakPrice = parseFloat(breakItem.prc) * 100;
        const adjustedBasePrice = getCartTransformUnitBasePriceCents(breakPrice, breakItem.qty, variantId, {
            sizeMultiplier
        });
        const finalPrice = adjustedBasePrice + additionalPrice;
        td.textContent = formatMoney(finalPrice);
        retailRow.appendChild(td);
    });

    tbody.appendChild(retailRow);
}

function createDistributorPriceRow(tbody, priceBreaks, additionalPrice = 0, sizeMultiplier = 1, variantId = null) {
    // For logged-in users: show distributor prices (with discount applied)
    const distributorRow = document.createElement('tr');
    const distributorLabelTd = document.createElement('td');
    distributorLabelTd.textContent = getTranslatedText('general.quantity_discounts.distributor_price') || 'Distributor Price';
    distributorRow.appendChild(distributorLabelTd);

    priceBreaks.forEach(breakItem => {
        const td = document.createElement('td');
        const breakPrice = parseFloat(breakItem.prc) * 100;
        const adjustedBasePrice = getCartTransformUnitBasePriceCents(breakPrice, breakItem.qty, variantId, {
            applyDistributorDiscount: true,
            sizeMultiplier
        });
        const finalPrice = adjustedBasePrice + additionalPrice;
        td.textContent = formatMoney(finalPrice);

        distributorRow.appendChild(td);
    });

    tbody.appendChild(distributorRow);
}

function createSavingsPercentageRow(tbody, priceBreaks, sizeMultiplier = 1, variantId = null) {
    const savingsRow = document.createElement('tr');
    savingsRow.className = 'save-row';
    const savingsLabelTd = document.createElement('td');
    savingsLabelTd.textContent = getTranslatedText('general.quantity_discounts.savings_percentage') || 'Savings %';
    savingsRow.appendChild(savingsLabelTd);

    const isCustomerLoggedIn = window.themeVariables && window.themeVariables.settings.isCustomerLoggedIn;
    const isDistributor = Boolean(window.themeVariables?.settings?.isDistributor);
    const distributorDiscount = window.themeVariables?.settings?.distributorDiscount || 0;

    // Get first quantity price for comparison (using original prices, not including decoration additions)
    const firstBreakQty = priceBreaks[0].qty;
    const firstBreakPrice = getCartTransformUnitBasePriceCents(parseFloat(priceBreaks[0].prc) * 100, firstBreakQty, variantId, {
        sizeMultiplier
    });

    priceBreaks.forEach(breakItem => {
        const td = document.createElement('td');
        const rawBreakPrice = parseFloat(breakItem.prc) * 100;
        const breakPrice = getCartTransformUnitBasePriceCents(parseFloat(breakItem.prc) * 100, breakItem.qty, variantId, {
            sizeMultiplier
        });
        const qty = breakItem.qty;

        let savingsPercentage = 0;

        if (isCustomerLoggedIn && isDistributor && distributorDiscount > 0) {
            // For logged-in users: compare distributor prices (using original prices)
            const firstDistributorPrice = getCartTransformUnitBasePriceCents(parseFloat(priceBreaks[0].prc) * 100, firstBreakQty, variantId, {
                applyDistributorDiscount: true,
                sizeMultiplier
            });
            const currentDistributorPrice = getCartTransformUnitBasePriceCents(rawBreakPrice, breakItem.qty, variantId, {
                applyDistributorDiscount: true,
                sizeMultiplier
            });

            const firstTotal = firstDistributorPrice * qty;
            const currentTotal = currentDistributorPrice * qty;
            const totalSavings = firstTotal - currentTotal;
            savingsPercentage = Math.round((totalSavings / firstTotal) * 100);
        } else {
            // For non-logged-in users: compare regular prices (using original prices)
            const firstTotal = firstBreakPrice * qty;
            const currentTotal = breakPrice * qty;
            const totalSavings = firstTotal - currentTotal;
            savingsPercentage = Math.round((totalSavings / firstTotal) * 100);
        }

        td.textContent = savingsPercentage + '%';
        savingsRow.appendChild(td);
    });

    tbody.appendChild(savingsRow);
}

function getCurrentProductQuantity() {
    const variantPicker = document.querySelector('.product__info variant-picker') || document.querySelector('variant-picker[form-id]');
    const formId = variantPicker?.getAttribute('form-id');

    let quantityInput = null;
    if (formId) {
        quantityInput = document.querySelector(`input[name="quantity"][form="${formId}"]`);
    }

    if (!quantityInput) {
        quantityInput = document.querySelector('.product-form input[name="quantity"]') || document.querySelector('input[name="quantity"]');
    }

    return parseInt(quantityInput?.value, 10) || 1;
}

function getVariantPriceBreaks(variantId) {
    if (!variantId) {
        return [];
    }

    const quantityDiscountsWrapper = document.querySelector('.quantity-discounts-wrapper');
    const variantDataSpan = quantityDiscountsWrapper?.querySelector(`[data-variant-id="${variantId}"]`);
    const priceBreaksJson = variantDataSpan?.getAttribute('data-price-breaks');

    if (!priceBreaksJson) {
        return [];
    }

    try {
        const priceBreaks = JSON.parse(priceBreaksJson);
        return Array.isArray(priceBreaks) ? priceBreaks : [];
    } catch (error) {
        return [];
    }
}

function getVariantsMetafieldsData() {
    const variantsMetafieldsScripts = document.querySelectorAll('script[data-variants-metafields]');
    const variantsMetafields = {};

    variantsMetafieldsScripts.forEach((variantsMetafieldsScript) => {
        try {
            const parsedMetafields = JSON.parse(variantsMetafieldsScript.textContent);
            if (parsedMetafields && typeof parsedMetafields === 'object') {
                Object.assign(variantsMetafields, parsedMetafields);
            }
        } catch (error) {
            console.error('Error parsing variants metafields data:', error);
        }
    });

    return variantsMetafields;
}

function getVariantMetafieldsData(variantId) {
    if (!variantId) {
        return null;
    }

    const variantsMetafields = getVariantsMetafieldsData();
    return variantsMetafields[String(variantId)] || variantsMetafields[variantId] || null;
}

function getCurrentProductVariantId() {
    const variantPicker = document.querySelector('.product__info variant-picker') || document.querySelector('variant-picker[form-id]');
    const formId = variantPicker?.getAttribute('form-id');
    const productForm = formId ? document.getElementById(formId) : document.querySelector('form[is="product-form"]');
    const formVariantInput = productForm?.querySelector('input[name="id"], select[name="id"]');

    if (formVariantInput?.value) {
        return formVariantInput.value;
    }

    const variantScript = variantPicker?.querySelector('script[data-variant]') || document.querySelector('variant-picker script[data-variant], script[data-variant]');
    if (variantScript) {
        try {
            const currentVariant = JSON.parse(variantScript.textContent);
            if (currentVariant?.id) {
                return currentVariant.id;
            }
        } catch (error) {
            console.error('Error parsing variant data:', error);
        }
    }

    const selectedVariantElement = document.querySelector('[data-selected-variant], [data-selected-or-first-available-variant-id]');
    return selectedVariantElement?.getAttribute('data-selected-variant') || selectedVariantElement?.getAttribute('data-selected-or-first-available-variant-id') || null;
}

function getMetafieldRawValue(value) {
    return value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value') ? value.value : value;
}

function parseMetafieldBooleanValue(value) {
    const metafieldValue = getMetafieldRawValue(value);

    if (typeof metafieldValue === 'string') {
        const normalizedValue = metafieldValue.trim().toLowerCase();
        return normalizedValue === 'true' || normalizedValue === '1' || normalizedValue === 'yes';
    }

    if (typeof metafieldValue === 'number') {
        return metafieldValue === 1;
    }

    return metafieldValue === true;
}

function isCurrentVariantBespokeEnabled() {
    const metafields = getVariantMetafieldsData(getCurrentProductVariantId());
    return Boolean(metafields && parseMetafieldBooleanValue(metafields.enable_bespoke));
}

function parseMoneyMetafieldCents(value) {
    const metafieldValue = getMetafieldRawValue(value);

    if (metafieldValue && typeof metafieldValue === 'object') {
        if (Object.prototype.hasOwnProperty.call(metafieldValue, 'amount')) {
            const amount = Number(String(metafieldValue.amount).replace(/[^0-9.-]/g, ''));
            return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
        }

        if (Object.prototype.hasOwnProperty.call(metafieldValue, 'cents')) {
            const cents = Number(metafieldValue.cents);
            return Number.isFinite(cents) ? Math.round(cents) : 0;
        }
    }

    if (typeof metafieldValue === 'string') {
        const normalizedValue = metafieldValue.trim();
        if (normalizedValue === '') {
            return 0;
        }

        const amount = Number(normalizedValue.replace(/[^0-9.-]/g, ''));
        return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
    }

    const numericValue = Number(metafieldValue);
    return Number.isFinite(numericValue) ? Math.round(numericValue) : 0;
}

function getMinimumBespokePriceCents(variantId) {
    const metafields = getVariantMetafieldsData(variantId);

    if (!metafields || !parseMetafieldBooleanValue(metafields.enable_bespoke)) {
        return 0;
    }

    const variantMinimumPrice = parseMoneyMetafieldCents(metafields.minimum_bespoke_price);
    const productMinimumPrice = parseMoneyMetafieldCents(metafields.product_minimum_bespoke_price);
    const minimumPrice = variantMinimumPrice > 0 ? variantMinimumPrice : productMinimumPrice;

    return minimumPrice > 0 ? minimumPrice : 0;
}

function getMinimumBespokeUnitBasePriceCents(basePriceCents, quantity = 1, variantId = null) {
    const normalizedBasePrice = Number(basePriceCents || 0);
    const minimumPrice = getMinimumBespokePriceCents(variantId);
    const normalizedQuantity = parseInt(quantity, 10);
    const itemQuantity = Number.isFinite(normalizedQuantity) && normalizedQuantity > 0 ? normalizedQuantity : 1;

    if (!Number.isFinite(normalizedBasePrice) || normalizedBasePrice <= 0 || minimumPrice <= 0) {
        return Number.isFinite(normalizedBasePrice) ? normalizedBasePrice : 0;
    }

    if (normalizedBasePrice * itemQuantity >= minimumPrice) {
        return normalizedBasePrice;
    }

    return Math.ceil(minimumPrice / itemQuantity);
}

function getSelectedVariantNormalBasePriceCents(variant, quantity = getCurrentProductQuantity()) {
    const variantPriceCents = Number(variant?.price || 0);
    const applicableBreak = getApplicablePriceBreak(getVariantPriceBreaks(variant?.id), quantity);
    const applicableBreakPriceCents = parseFloat(applicableBreak?.prc) * 100;

    if (Number.isFinite(applicableBreakPriceCents) && applicableBreakPriceCents > 0) {
        return applicableBreakPriceCents;
    }

    return Number.isFinite(variantPriceCents) ? variantPriceCents : 0;
}

function getSelectedVariantBasePriceCents(variant, quantity = getCurrentProductQuantity()) {
    return getCartTransformUnitBasePriceCents(
        getSelectedVariantNormalBasePriceCents(variant, quantity),
        quantity,
        variant?.id
    );
}

function getCurrentProductVariant() {
    const variantPicker = document.querySelector('.product__info variant-picker') || document.querySelector('variant-picker');
    const variantScript = variantPicker?.querySelector('script[data-variant]');

    if (!variantScript) {
        return null;
    }

    try {
        return JSON.parse(variantScript.textContent);
    } catch (error) {
        console.error('Error parsing current variant data:', error);
        return null;
    }
}

function updateProductMetaMinimumBespokePriceDisplay(variant = getCurrentProductVariant()) {
    const priceContainer = document.querySelector('[data-product-price-list-container]');
    const priceElement = priceContainer?.querySelector('.price--large');

    if (!priceContainer || !priceElement || priceContainer.hasAttribute('hidden') || !variant) {
        return;
    }

    if (!priceElement.dataset.originalPriceHtml) {
        priceElement.dataset.originalPriceHtml = priceElement.innerHTML;
    }

    const quantity = getCurrentProductQuantity();
    const normalBasePrice = getSelectedVariantNormalBasePriceCents(variant, quantity);
    const finalPrice = getCartTransformUnitBasePriceCents(normalBasePrice, quantity, variant.id, {
        applyDistributorDiscount: true
    });
    const originalComparablePrice = applyDistributorDiscountToCents(normalBasePrice);

    if (finalPrice <= 0 || finalPrice === originalComparablePrice) {
        priceElement.innerHTML = priceElement.dataset.originalPriceHtml;
        return;
    }

    priceElement.textContent = formatMoney(finalPrice);
}

// Store current pricing context for quantity-based recalculation
let currentPriceBreaks = [];
let currentAdditionalPrice = 0;
let currentPricingVariantId = null;

function getApplicablePriceBreak(priceBreaks, quantity = 1) {
    if (!Array.isArray(priceBreaks) || priceBreaks.length === 0) {
        return null;
    }

    const targetQuantity = Number.isFinite(parseInt(quantity, 10)) && parseInt(quantity, 10) > 0 ? parseInt(quantity, 10) : 1;

    let applicableBreak = priceBreaks[0];
    let bestQty = Number.isFinite(parseInt(applicableBreak?.qty, 10)) ? parseInt(applicableBreak.qty, 10) : 1;

    priceBreaks.forEach((breakItem) => {
        const breakQty = parseInt(breakItem?.qty, 10);

        if (!Number.isFinite(breakQty)) {
            return;
        }

        if (breakQty <= targetQuantity && breakQty >= bestQty) {
            applicableBreak = breakItem;
            bestQty = breakQty;
        }
    });

    return applicableBreak;
}

function getCustomSizingMultiplier() {
    const customSizingSection = document.querySelector('[data-product-custom-sizing]');
    const thicknessInput = document.querySelector('[data-custom-sizing-thickness]');
    const widthInput = document.querySelector('[data-custom-sizing-width]');
    const heightInput = document.querySelector('[data-custom-sizing-height]');

    if (!customSizingSection || customSizingSection.hasAttribute('hidden')) {
        return 1;
    }

    const width = parseFloat(widthInput?.value);
    const height = parseFloat(heightInput?.value);

    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return 1;
    }

    if (!thicknessInput || thicknessInput.disabled) {
        return width * height;
    }

    const thickness = parseFloat(thicknessInput.value);
    if (!Number.isFinite(thickness) || thickness <= 0) {
        return width * height;
    }

    return width * height * thickness;
}

function getCartTransformUnitBasePriceCents(basePriceCents, quantity = 1, variantId = null, options = {}) {
    const normalizedBasePrice = Number(basePriceCents || 0);

    if (!Number.isFinite(normalizedBasePrice) || normalizedBasePrice <= 0) {
        return 0;
    }

    const normalizedSizeMultiplier = Number(options.sizeMultiplier);
    const sizeMultiplier = Number.isFinite(normalizedSizeMultiplier) && normalizedSizeMultiplier > 0
        ? normalizedSizeMultiplier
        : getCustomSizingMultiplier();
    let transformedPrice = Math.round(normalizedBasePrice * sizeMultiplier);
    transformedPrice = getMinimumBespokeUnitBasePriceCents(transformedPrice, quantity, variantId);

    if (options.applyDistributorDiscount) {
        transformedPrice = applyDistributorDiscountToCents(transformedPrice);
    }

    return transformedPrice;
}

function applyDistributorDiscountToCents(amountCents) {
    const normalizedAmount = Number(amountCents || 0);
    const isDistributor = Boolean(window.themeVariables?.settings?.isDistributor);
    const distributorDiscount = Number(window.themeVariables?.settings?.distributorDiscount || 0);

    if (!isDistributor || !Number.isFinite(normalizedAmount) || normalizedAmount <= 0 || !Number.isFinite(distributorDiscount) || distributorDiscount <= 0) {
        return normalizedAmount;
    }

    const discountAmount = Math.round(normalizedAmount * distributorDiscount / 100);
    return normalizedAmount - discountAmount;
}

/**
 * Update the single unit price display (data-table-price-value)
 * Gets the price for qty = 1 (first price break) and includes decoration additions
 * Multiplies by quantity if provided
 * Shows/hides the label based on whether there's a value
 * @param {Array} priceBreaks - Array of price break objects
 * @param {{ perUnitPriceCents: number, oneTimePriceCents: number }|number} additionalPrice - Additional price details from decorations in cents
 * @param {number} quantity - Quantity to multiply the price (default: 1)
 */
function updateTablePriceValue(priceBreaks, additionalPrice = 0, quantity = 1, variantId = null) {
    const priceValueElement = document.querySelector('[data-table-price-value]');
    const priceLabelElement = document.querySelector('[data-table-price-label]');
    const priceContainerElement = document.querySelector('[data-table-price]');
    
    if (!priceValueElement || !priceBreaks || priceBreaks.length === 0) {
        // Hide label and container if no price breaks
        if (priceLabelElement) {
            priceLabelElement.style.display = 'none';
        }
        if (priceContainerElement) {
            priceContainerElement.style.display = 'none';
        }
        return;
    }

    // Store current pricing context so quantity changes can recalculate from the same table data
    currentPriceBreaks = priceBreaks;
    currentAdditionalPrice = additionalPrice;
    currentPricingVariantId = variantId;

    // Get the applicable price break based on current quantity
    const applicableBreak = getApplicablePriceBreak(priceBreaks, quantity);
    if (!applicableBreak) {
        // Hide label and container if no applicable break
        if (priceLabelElement) {
            priceLabelElement.style.display = 'none';
        }
        if (priceContainerElement) {
            priceContainerElement.style.display = 'none';
        }
        return;
    }

    const perUnitAdditionalPrice = typeof additionalPrice === 'number' ? additionalPrice : (additionalPrice?.perUnitPriceCents || 0);
    const breakPrice = parseFloat(applicableBreak.prc) * 100; // Convert to cents

    if (!Number.isFinite(breakPrice)) {
        if (priceLabelElement) {
            priceLabelElement.style.display = 'none';
        }
        if (priceContainerElement) {
            priceContainerElement.style.display = 'none';
        }
        return;
    }

    const adjustedBasePrice = getCartTransformUnitBasePriceCents(breakPrice, quantity, variantId);
    const discountedBasePrice = getCartTransformUnitBasePriceCents(breakPrice, quantity, variantId, {
        applyDistributorDiscount: true
    });
    const priceWithAdditional = adjustedBasePrice + perUnitAdditionalPrice;

    const isCustomerLoggedIn = window.themeVariables && window.themeVariables.settings.isCustomerLoggedIn;
    const isDistributor = Boolean(window.themeVariables?.settings?.isDistributor);
    let singleUnitPrice = priceWithAdditional;

    // Decoration additional pricing is already distributor-adjusted upstream.
    // Only the base break price should receive the distributor discount here.
    if (isCustomerLoggedIn && isDistributor && window.themeVariables && window.themeVariables.settings.distributorDiscount) {
        singleUnitPrice = discountedBasePrice + perUnitAdditionalPrice;
    }

    // Final price follows the same per-piece cost as the quantity-discounts table tier
    const finalPrice = singleUnitPrice;

    // Format and display the price
    priceValueElement.textContent = formatMoney(finalPrice);

    // Show label and container when we have a value
    if (priceLabelElement) {
        priceLabelElement.style.display = '';
    }
    if (priceContainerElement) {
        priceContainerElement.style.display = '';
    }
}

/**
 * Update the table price value based on current quantity
 * Uses the stored baseSingleUnitPrice and multiplies by the current quantity
 * @param {number} quantityOverride - Optional quantity from the triggering control
 */
function updateTablePriceValueByQuantity(quantityOverride) {
    let quantity = parseInt(quantityOverride, 10);

    if (!Number.isFinite(quantity) || quantity <= 0) {
        quantity = getCurrentProductQuantity();
    }

    if (Array.isArray(currentPriceBreaks) && currentPriceBreaks.length > 0) {
        updateTablePriceValue(currentPriceBreaks, currentAdditionalPrice, quantity, currentPricingVariantId);
    }

    updateCustomSizingPerItemCost();
}

function getTranslatedText(key) {
    if (window.themeVariables && window.themeVariables.strings) {
        const translationKey = key.replace(/\./g, '_');
        if (window.themeVariables.strings[translationKey]) {
            return window.themeVariables.strings[translationKey];
        }
    }

    // Fallback translations
    const translations = {
        'general.quantity_discounts.title': 'Quantity Discounts',
        'general.quantity_discounts.qty': 'Qty',
        'general.quantity_discounts.price': 'Price',
        'general.quantity_discounts.retail_price': 'Retail Price',
        'general.quantity_discounts.distributor_price': 'Distributor Price',
        'general.quantity_discounts.savings_percentage': 'Savings %',
        'general.quantity_discounts.save': 'Save',
        'general.production_lead_time.range': 'Average production time is @prod_time_low_value@ to @prod_time_hi_value@ days.',
        'general.production_lead_time.low_only': 'Average production time is @prod_time_low_value@ days.',
        'general.production_lead_time.high_only': 'Average production time is @prod_time_hi_value@ days.'
    };

    return translations[key] || key;
}

function formatMoney(amount) {
    const dollars = amount / 100;

    if (window.themeVariables && window.themeVariables.settings.moneyFormat) {
        const moneyFormat = window.themeVariables.settings.moneyFormat;

        const formattedAmount = dollars.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        return moneyFormat.replace('{{amount}}', formattedAmount);
    }

    const currency = window.themeVariables?.settings?.cartCurrency || 'USD';
    const formatter = new Intl.NumberFormat(navigator.language || 'en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    return formatter.format(dollars);
}

function initializeMobileDecorationDescription() {
    const decorationCards = document.querySelectorAll('.decoration-method-card');
    const descriptionElement = document.getElementById('decoration-method-description');
    const decorationMethodsData = window.decorationMethodsData || [];

    if (decorationCards.length === 0 || !descriptionElement) return;

    decorationCards.forEach(card => {
        card.addEventListener('click', function() {
            if (window.innerWidth > 1024) return;

            const method = this.getAttribute('data-method');
            const methodData = decorationMethodsData.find(m => m.method === method);

            if (methodData && methodData.engraving_method_description) {
                descriptionElement.innerHTML = `<p>${methodData.engraving_method_description}</p>`;
            }
        });
    });
}

function showNotesInput() {
    if (!document.querySelector('[data-add-note-trigger]')) {
        return;
    }

    const addNoteTrigger = document.querySelector('[data-add-note-trigger]');
    addNoteTrigger.addEventListener('click', function() {
        const notesSection = document.querySelector('[data-notes-section]');
        notesSection.removeAttribute('hidden');
        addNoteTrigger.setAttribute('important-hidden', 'true');
    });
}

function getSelectedDecorationMethod() {
    const selectedDecorationMethodInput = document.querySelector('input[name="properties[Decoration Method]"]:checked');
    return selectedDecorationMethodInput ? (selectedDecorationMethodInput.getAttribute('data-method') || selectedDecorationMethodInput.value || '') : '';
}

function isDecorationDimensionInputActive(inputElement) {
    if (!(inputElement instanceof HTMLInputElement) || inputElement.disabled || inputElement.closest('[hidden]')) {
        return false;
    }

    if (inputElement.matches('[data-width-input], [data-height-input], [data-width-input-color], [data-height-input-color]')) {
        const selectedMethod = getSelectedDecorationMethod();
        return !!selectedMethod && selectedMethod !== 'Blank';
    }

    return true;
}

function clampDecorationDimensionInputValue(inputElement) {
    if (!(inputElement instanceof HTMLInputElement) || inputElement.value === '') {
        updateDecorationDimensionInputValidity(inputElement);
        return false;
    }

    const precisionLimited = limitDecorationDimensionInputPrecision(inputElement);
    const currentValue = parseFloat(inputElement.value);
    if (!Number.isFinite(currentValue)) {
        updateDecorationDimensionInputValidity(inputElement);
        return false;
    }

    const minValue = parseFloat(inputElement.getAttribute('min'));
    const maxValue = parseFloat(inputElement.getAttribute('max'));
    let clampedValue = currentValue;
    let didChange = precisionLimited;

    if (Number.isFinite(minValue) && clampedValue < minValue) {
        clampedValue = minValue;
        didChange = true;
    }

    if (Number.isFinite(maxValue) && clampedValue > maxValue) {
        clampedValue = maxValue;
        didChange = true;
    }

    if (didChange) {
        inputElement.value = formatDecorationDimensionValue(clampedValue);
    }

    updateDecorationDimensionInputValidity(inputElement);
    return didChange;
}

function formatDecorationDimensionValue(value) {
    if (!Number.isFinite(value)) {
        return '';
    }

    if (Number.isInteger(value)) {
        return String(value);
    }

    return value.toFixed(2).replace(/\.0+$/, '').replace(/(\.\d*[1-9])0$/, '$1');
}

function limitDecorationDimensionInputPrecision(inputElement) {
    if (!(inputElement instanceof HTMLInputElement)) {
        return false;
    }

    const rawValue = inputElement.value;
    if (!rawValue || !rawValue.includes('.')) {
        return false;
    }

    const [integerPart, decimalPart = ''] = rawValue.split('.');
    if (decimalPart.length <= 2) {
        return false;
    }

    inputElement.value = `${integerPart}.${decimalPart.slice(0, 2)}`;
    return true;
}

function updateDecorationDimensionInputValidity(inputElement) {
    if (!(inputElement instanceof HTMLInputElement)) {
        return;
    }

    const rawValue = inputElement.value;
    const hasTooManyDecimals = rawValue.includes('.') && rawValue.split('.')[1].length > 2;

    if (hasTooManyDecimals) {
        inputElement.setCustomValidity('Use no more than 2 decimal places.');
        inputElement.setAttribute('aria-invalid', 'true');
        inputElement.title = 'Use no more than 2 decimal places.';
        return;
    }

    inputElement.setCustomValidity('');
    inputElement.removeAttribute('aria-invalid');
    inputElement.removeAttribute('title');
}

function updateDecorationDimensionValidationState() {
    const addToCartButton = document.getElementById('AddToCart');
    if (!addToCartButton) {
        return;
    }

    const dimensionInputs = Array.from(document.querySelectorAll('.product-decoration-dimensions-row-label-wrapper input'));
    const hasInvalidInputs = dimensionInputs.some((inputElement) => {
        if (!isDecorationDimensionInputActive(inputElement) || inputElement.value === '') {
            updateDecorationDimensionInputValidity(inputElement);
            return false;
        }

        updateDecorationDimensionInputValidity(inputElement);
        return !inputElement.validity.valid;
    });

    if (hasInvalidInputs) {
        addToCartButton.disabled = true;
        addToCartButton.setAttribute('data-decoration-dimensions-invalid', 'true');
        return;
    }

    addToCartButton.removeAttribute('data-decoration-dimensions-invalid');

    const variantScript = document.querySelector('variant-picker script[data-variant], script[data-variant]');
    if (!variantScript) {
        return;
    }

    try {
        const currentVariant = JSON.parse(variantScript.textContent);
        addToCartButton.disabled = !currentVariant || currentVariant.available === false;
    } catch (error) {
        console.error('Error parsing variant data for dimension validation:', error);
    }
}

// ------------------------------------------------------------------------------------------------------------
// DECORATION INPUT LIMITS UPDATE ON VARIANT CHANGE
// ------------------------------------------------------------------------------------------------------------

function updateDecorationInputLimits(variant) {
    const widthInput = document.querySelector('[data-width-input], [data-width-input-color]');
    const heightInput = document.querySelector('[data-height-input], [data-height-input-color]');
    const widthLabel = document.querySelector('[data-width-label]');
    const heightLabel = document.querySelector('[data-height-label]');
    const customSizingSection = document.querySelector('[data-product-custom-sizing]');
    const customSizingWidthInput = document.querySelector('[data-custom-sizing-width]');
    const customSizingHeightInput = document.querySelector('[data-custom-sizing-height]');
    const customSizingThicknessInput = document.querySelector('[data-custom-sizing-thickness]');
    const customSizingThicknessWrapper = document.querySelector('[data-custom-sizing-thickness-wrapper]');
    const customSizingWidthLabel = document.querySelector('[data-custom-sizing-width-label]');
    const customSizingHeightLabel = document.querySelector('[data-custom-sizing-height-label]');
    const customSizingThicknessLabel = document.querySelector('[data-custom-sizing-thickness-label]');

    if (!variant) {
        return;
    }

    // Get metafields for the current variant
    const metafields = getVariantMetafieldsData(variant.id);
    if (!metafields) {
        return;
    }

    const maxImprint1 = metafields.max_imprint_1;
    const maxImprint1Type = metafields.max_imprint1_type;
    const maxImprint2 = metafields.max_imprint2;
    const minImprint1 = metafields.min_imprint_1;
    const minImprint2 = metafields.min_imprint2;
    const bespokeWidthMin = metafields.bespoke_width_min;
    const bespokeWidthMax = metafields.bespoke_width_max;
    const bespokeHeightMin = metafields.bespoke_height_min;
    const bespokeHeightMax = metafields.bespoke_height_max;
    const enableBespoke = metafields.enable_bespoke;
    const minThickness = metafields.min_thickness;
    const maxThickness = metafields.max_thickness;
    const unitThickness = metafields.unit_thickness || 'in.';

    const hasValidNumber = (value) => value !== null && value !== '' && !isNaN(value);
    const updateLabelText = (label, minValue, maxValue) => {
        if (!label) {
            return;
        }

        if (!label.dataset.baseLabel) {
            label.dataset.baseLabel = label.textContent.trim();
        }

        const baseLabel = label.dataset.baseLabel;
        let rangeText = '';

        if (hasValidNumber(minValue) && hasValidNumber(maxValue)) {
            rangeText = ` (${minValue} - ${maxValue} in.)`;
        } else if (hasValidNumber(minValue)) {
            rangeText = ` (min ${minValue} in.)`;
        } else if (hasValidNumber(maxValue)) {
            rangeText = ` (max ${maxValue} in.)`;
        }

        if(rangeText != '') {
            label.textContent = `${baseLabel.replace("(in)", "")}${rangeText}`;
        }
    };

    const updateThicknessLabelText = (label, minValue, maxValue, unitValue) => {
        if (!label) {
            return;
        }

        if (!label.dataset.baseLabel) {
            label.dataset.baseLabel = label.textContent.trim();
        }

        const baseLabel = label.dataset.baseLabel;
        const normalizedUnit = (unitValue || 'in').toString().trim();
        const escapedUnit = normalizedUnit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const normalizedBaseLabel = baseLabel
            .replace(new RegExp(`\\s*\\(\\s*${escapedUnit}\\.?\\s*\\)\\s*$`, 'i'), '')
            .replace(/\s*\(\s*in\.?\s*\)\s*$/i, '')
            .trim();

        const formatRangeNumber = (value) => {
            const numericValue = parseFloat(value);

            if (!Number.isFinite(numericValue)) {
                return value;
            }

            return Number.isInteger(numericValue) ? `${numericValue}` : `${numericValue}`;
        };

        const formattedMin = formatRangeNumber(minValue);
        const formattedMax = formatRangeNumber(maxValue);
        let rangeText = '';

        if (hasValidNumber(minValue) && hasValidNumber(maxValue)) {
            rangeText = ` (${formattedMin} - ${formattedMax} ${normalizedUnit})`;
        } else if (hasValidNumber(minValue)) {
            rangeText = ` (min ${formattedMin} ${normalizedUnit})`;
        } else if (hasValidNumber(maxValue)) {
            rangeText = ` (max ${formattedMax} ${normalizedUnit})`;
        }

        label.textContent = rangeText ? `${normalizedBaseLabel}${rangeText}` : normalizedBaseLabel;
    };

    const setInputLimits = (input, minValue, maxValue) => {
        if (!input) {
            return;
        }

        if (hasValidNumber(maxValue)) {
            input.setAttribute('max', maxValue);
        } else {
            input.removeAttribute('max');
        }

        if (hasValidNumber(minValue)) {
            input.setAttribute('min', minValue);
        } else {
            input.removeAttribute('min');
        }

        clampDecorationDimensionInputValue(input);
    };

    // Determine which metafield corresponds to width and height
    let maxWidth, maxHeight;
    let minWidth, minHeight;

    if (maxImprint1Type === 'Width') {
        // If imprint1 type is Width, then imprint1 is width and imprint2 is height
        maxWidth = maxImprint1;
        maxHeight = maxImprint2;
        minWidth = minImprint1;
        minHeight = minImprint2;
    } else {
        // If imprint1 type is not Width, then imprint1 is height and imprint2 is width
        maxHeight = maxImprint1;
        maxWidth = maxImprint2;
        minHeight = minImprint1;
        minWidth = minImprint2;
    }

    setInputLimits(widthInput, minWidth, maxWidth);
    setInputLimits(heightInput, minHeight, maxHeight);
    applyDecorationDimensionMinimumDefaults();

    updateLabelText(widthLabel, minWidth, maxWidth);
    updateLabelText(heightLabel, minHeight, maxHeight);

    const customSizingMinWidth = hasValidNumber(bespokeWidthMin) ? bespokeWidthMin : minWidth;
    const customSizingMaxWidth = hasValidNumber(bespokeWidthMax) ? bespokeWidthMax : maxWidth;
    const customSizingMinHeight = hasValidNumber(bespokeHeightMin) ? bespokeHeightMin : minHeight;
    const customSizingMaxHeight = hasValidNumber(bespokeHeightMax) ? bespokeHeightMax : maxHeight;

    if (customSizingWidthInput) {
        setInputLimits(customSizingWidthInput, customSizingMinWidth, customSizingMaxWidth);
    }

    if (customSizingHeightInput) {
        setInputLimits(customSizingHeightInput, customSizingMinHeight, customSizingMaxHeight);
    }

    updateLabelText(customSizingWidthLabel, customSizingMinWidth, customSizingMaxWidth);
    updateLabelText(customSizingHeightLabel, customSizingMinHeight, customSizingMaxHeight);

    const hasThickness = hasValidNumber(minThickness) && hasValidNumber(maxThickness);
    const parseMetafieldBoolean = (value) => {
        const metafieldValue = value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value') ? value.value : value;

        if (typeof metafieldValue === 'string') {
            const normalizedValue = metafieldValue.trim().toLowerCase();
            return normalizedValue === 'true' || normalizedValue === '1' || normalizedValue === 'yes';
        }

        if (typeof metafieldValue === 'number') {
            return metafieldValue === 1;
        }

        return metafieldValue === true;
    };
    const isBespokeEnabled = parseMetafieldBoolean(enableBespoke);

    if (customSizingSection) {
        if (isBespokeEnabled) {
            customSizingSection.removeAttribute('hidden');
        } else {
            customSizingSection.setAttribute('hidden', 'true');
        }
    }

    if (customSizingThicknessInput) {
        setInputLimits(customSizingThicknessInput, minThickness, maxThickness);
        customSizingThicknessInput.disabled = !hasThickness;

        if (hasThickness && !customSizingThicknessInput.value && hasValidNumber(minThickness)) {
            customSizingThicknessInput.value = minThickness;
        } else if (!hasThickness) {
            customSizingThicknessInput.value = '';
        }
    }

    if (customSizingThicknessWrapper) {
        customSizingThicknessWrapper.hidden = !hasThickness;
    }

    updateThicknessLabelText(customSizingThicknessLabel, minThickness, maxThickness, unitThickness);

    if (isBespokeEnabled) {
        if (customSizingWidthInput && !customSizingWidthInput.value && hasValidNumber(customSizingMinWidth)) {
            customSizingWidthInput.value = customSizingMinWidth;
        }

        if (customSizingHeightInput && !customSizingHeightInput.value && hasValidNumber(customSizingMinHeight)) {
            customSizingHeightInput.value = customSizingMinHeight;
        }
    }

    toggleDecorationDimensionLock(false);
    updateDecorationSizeSyncNote(false);
    updateDecorationDimensionsRowVisibility();
    updateCustomSizingPerItemCost();
    updateDecorationDimensionValidationState();
}

function applyDecorationDimensionMinimumDefaults() {
    const selectedDecorationMethodInput = document.querySelector('input[name="properties[Decoration Method]"]:checked');
    const selectedMethod = selectedDecorationMethodInput ? (selectedDecorationMethodInput.getAttribute('data-method') || selectedDecorationMethodInput.value || '') : '';

    if (!selectedMethod || selectedMethod === 'Blank') {
        return;
    }

    const decorateMethodSection = document.querySelector('[data-decorate-method-section]');
    if (!decorateMethodSection) {
        return;
    }

    const applyMinimumValue = (selector) => {
        const inputElement = decorateMethodSection.querySelector(selector);
        if (!inputElement) {
            return;
        }

        const minValue = parseFloat(inputElement.getAttribute('min'));
        if (!Number.isFinite(minValue)) {
            return;
        }

        const currentValue = parseFloat(inputElement.value);
        if (inputElement.value === '' || !Number.isFinite(currentValue) || currentValue < minValue) {
            inputElement.value = String(minValue);
            inputElement.dispatchEvent(new Event('input', { bubbles: true }));
            inputElement.dispatchEvent(new Event('change', { bubbles: true }));
        }
    };

    applyMinimumValue('[data-width-input], [data-width-input-color]');
    applyMinimumValue('[data-height-input], [data-height-input-color]');
    updateDecorationDimensionValidationState();
}

function updateDecorationDimensionsRowVisibility() {
    const decorationDimensionsRow = document.querySelector('.product-decoration-dimensions-row');
    if (!decorationDimensionsRow) {
        return;
    }

    const selectedDecorationMethodInput = document.querySelector('input[name="properties[Decoration Method]"]:checked');
    const selectedMethod = selectedDecorationMethodInput ? (selectedDecorationMethodInput.getAttribute('data-method') || selectedDecorationMethodInput.value || '') : '';
    const isVisible = !!selectedMethod && selectedMethod !== 'Blank';

    decorationDimensionsRow.style.display = isVisible ? '' : 'none';
    updateDecorationDimensionValidationState();
}

function updateCustomSizingPerItemCost() {
    const customSizingSection = document.querySelector('[data-product-custom-sizing]');
    const customSizingCostElement = document.querySelector('[data-custom-sizing-cost]');

    if (!customSizingSection || !customSizingCostElement || customSizingSection.hasAttribute('hidden')) {
        if (customSizingCostElement) {
            customSizingCostElement.hidden = true;
            customSizingCostElement.textContent = '';
        }
        return;
    }

    const customSizingWidthInput = document.querySelector('[data-custom-sizing-width]');
    const customSizingHeightInput = document.querySelector('[data-custom-sizing-height]');
    const customSizingThicknessInput = document.querySelector('[data-custom-sizing-thickness]');

    const width = parseFloat(customSizingWidthInput?.value);
    const height = parseFloat(customSizingHeightInput?.value);
    const rawThickness = parseFloat(customSizingThicknessInput?.value);
    const thickness = (Number.isFinite(rawThickness) && rawThickness > 0) ? rawThickness : 1;

    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        customSizingCostElement.hidden = true;
        customSizingCostElement.textContent = '';
        return;
    }

    const variantPicker = document.querySelector('variant-picker');
    const variantScript = variantPicker?.querySelector('script[data-variant]');

    if (!variantScript) {
        customSizingCostElement.hidden = true;
        customSizingCostElement.textContent = '';
        return;
    }

    let currentVariant;
    try {
        currentVariant = JSON.parse(variantScript.textContent);
    } catch (error) {
        customSizingCostElement.hidden = true;
        customSizingCostElement.textContent = '';
        return;
    }

    const priceBreaks = getVariantPriceBreaks(currentVariant.id);
    if (!Array.isArray(priceBreaks) || priceBreaks.length === 0) {
        customSizingCostElement.hidden = true;
        customSizingCostElement.textContent = '';
        return;
    }

    const currentQty = getCurrentProductQuantity();
    const applicableBreak = getApplicablePriceBreak(priceBreaks, currentQty) || priceBreaks[0];
    const basePriceCents = parseFloat(applicableBreak.prc) * 100;

    if (!Number.isFinite(basePriceCents) || basePriceCents <= 0) {
        customSizingCostElement.hidden = true;
        customSizingCostElement.textContent = '';
        return;
    }

    const totalPerItemCents = getCartTransformUnitBasePriceCents(basePriceCents, currentQty, currentVariant.id, {
        applyDistributorDiscount: true,
        sizeMultiplier: width * height * thickness
    });
    const totalPerItemText = typeof formatMoney === 'function' ? formatMoney(totalPerItemCents) : (totalPerItemCents / 100).toFixed(2);

    customSizingCostElement.textContent = `${totalPerItemText} per item`;
    customSizingCostElement.hidden = false;
}

function getDecorationDimensionInputs() {
    const widthInputs = document.querySelectorAll('[data-width-input], [data-width-input-color]');
    const heightInputs = document.querySelectorAll('[data-height-input], [data-height-input-color]');

    return { widthInputs, heightInputs };
}

function getOrCreateSyncedHiddenInput(inputElement, suffix) {
    if (!inputElement || !inputElement.name) {
        return null;
    }

    const hiddenInputSelector = `input[type="hidden"][data-decoration-dimension-sync="${suffix}"]`;
    let hiddenInput = document.querySelector(hiddenInputSelector);

    if (!hiddenInput) {
        hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.name = inputElement.name;
        hiddenInput.setAttribute('data-decoration-dimension-sync', suffix);
        inputElement.insertAdjacentElement('afterend', hiddenInput);
    }

    return hiddenInput;
}

function removeSyncedHiddenInput(suffix) {
    const hiddenInput = document.querySelector(`input[type="hidden"][data-decoration-dimension-sync="${suffix}"]`);
    if (hiddenInput) {
        hiddenInput.remove();
    }
}

function toggleDecorationDimensionLock(shouldLock) {
    const { widthInputs, heightInputs } = getDecorationDimensionInputs();

    const applyLock = (inputElement, suffix) => {
        if (!inputElement) {
            return;
        }

        if (shouldLock) {
            if (!inputElement.dataset.originalDisabled) {
                inputElement.dataset.originalDisabled = inputElement.disabled ? 'true' : 'false';
            }

            inputElement.disabled = true;

            const hiddenInput = getOrCreateSyncedHiddenInput(inputElement, suffix);
            if (hiddenInput) {
                hiddenInput.value = inputElement.value || '';
            }
        } else {
            if (Object.prototype.hasOwnProperty.call(inputElement.dataset, 'originalDisabled')) {
                inputElement.disabled = inputElement.dataset.originalDisabled === 'true';
                delete inputElement.dataset.originalDisabled;
            }
            removeSyncedHiddenInput(suffix);
        }
    };

    widthInputs.forEach((inputElement) => applyLock(inputElement, 'width'));
    heightInputs.forEach((inputElement) => applyLock(inputElement, 'height'));
}

function syncDecorationDimensionsFromCustomSizing() {
    const customSizingWidthInput = document.querySelector('[data-custom-sizing-width]');
    const customSizingHeightInput = document.querySelector('[data-custom-sizing-height]');

    if (!customSizingWidthInput && !customSizingHeightInput) {
        return;
    }

    const { widthInputs, heightInputs } = getDecorationDimensionInputs();

    if (customSizingWidthInput) {
        widthInputs.forEach((inputElement) => {
            inputElement.value = customSizingWidthInput.value;
            const hiddenInput = getOrCreateSyncedHiddenInput(inputElement, 'width');
            if (hiddenInput) {
                hiddenInput.value = customSizingWidthInput.value;
            }
            inputElement.dispatchEvent(new Event('input', { bubbles: true }));
            inputElement.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    if (customSizingHeightInput) {
        heightInputs.forEach((inputElement) => {
            inputElement.value = customSizingHeightInput.value;
            const hiddenInput = getOrCreateSyncedHiddenInput(inputElement, 'height');
            if (hiddenInput) {
                hiddenInput.value = customSizingHeightInput.value;
            }
            inputElement.dispatchEvent(new Event('input', { bubbles: true }));
            inputElement.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }
}

function updateDecorationSizeSyncNote(hasThickness) {
    const noteElement = document.querySelector('[data-decoration-size-sync-note]');
    if (!noteElement) {
        return;
    }

    const selectedDecorationMethodInput = document.querySelector('input[name="properties[Decoration Method]"]:checked');
    const selectedMethod = selectedDecorationMethodInput ? (selectedDecorationMethodInput.getAttribute('data-method') || selectedDecorationMethodInput.value || '') : '';
    const isBlankSelected = selectedMethod === 'Blank';

    noteElement.hidden = !(hasThickness && !isBlankSelected);
}

function initializeProductCustomSizingSync() {
    const customSizingWidthInput = document.querySelector('[data-custom-sizing-width]');
    const customSizingHeightInput = document.querySelector('[data-custom-sizing-height]');
    const customSizingThicknessInput = document.querySelector('[data-custom-sizing-thickness]');

    [customSizingWidthInput, customSizingHeightInput, customSizingThicknessInput].forEach((inputElement) => {
        if (!inputElement) {
            return;
        }

        inputElement.addEventListener('input', () => {
            updateCustomSizingPerItemCost();
            updateQuantityDiscountsTableFromDecoration();
        });

        inputElement.addEventListener('change', () => {
            updateCustomSizingPerItemCost();
            updateQuantityDiscountsTableFromDecoration();
        });
    });

    const decorationMethodInputs = document.querySelectorAll('input[name="properties[Decoration Method]"]');
    decorationMethodInputs.forEach((inputElement) => {
        inputElement.addEventListener('change', () => {
            updateDecorationSizeSyncNote(false);
            updateDecorationDimensionsRowVisibility();
            updateDecorationDimensionValidationState();
        });
    });

    updateDecorationDimensionsRowVisibility();
    updateCustomSizingPerItemCost();
    updateDecorationDimensionValidationState();
}

// Initialize decoration input limits on page load
document.addEventListener('DOMContentLoaded', function() {
    initializeProductCustomSizingSync();

    // Get the initial variant data from the script tag
    const variantScript = document.querySelector('script[data-variant]');
    if (variantScript) {
        try {
            const variantData = JSON.parse(variantScript.textContent);
            updateDecorationInputLimits(variantData);
            updateTemplateLink(variantData);
        } catch (error) {
            console.error('Error parsing initial variant data:', error);
        }
    }
});

// ------------------------------------------------------------------------------------------------------------
// TEMPLATE LINK UPDATE ON VARIANT CHANGE
// ------------------------------------------------------------------------------------------------------------

function updateTemplateLink(variant) {
    const templateLinkElement = document.querySelector('[data-template-link]');
    
    if (!templateLinkElement || !variant) {
        return;
    }

    // Find the variant template link data element
    const variantTemplateData = document.querySelector(`[data-template-variant-id="${variant.id}"]`);
    
    if (variantTemplateData && variantTemplateData.getAttribute('data-template-link')) {
        const templateLink = variantTemplateData.getAttribute('data-template-link');
        templateLinkElement.href = templateLink;
        templateLinkElement.style.display = '';
    } else {
        templateLinkElement.style.display = 'none';
    }
}

function initializeTextHeightInput() {
    const input = document.querySelector('[data-text-height-input]');

    if (!input) {
        return;
    }

    input.addEventListener('input', (e) => {
        // Get cursor position before modification
        let cursorPosition = e.target.selectionStart;
        
        // Get the current value and remove the inch symbol
        let rawValue = e.target.value.replace(/"/g, '');
        
        // Remove all non-numeric characters except dots
        let cleanValue = rawValue.replace(/[^0-9.]/g, '');
        
        // Prevent multiple dots
        const dotCount = (cleanValue.match(/\./g) || []).length;
        if (dotCount > 1) {
            const firstDotIndex = cleanValue.indexOf('.');
            cleanValue = cleanValue.substring(0, firstDotIndex + 1) + cleanValue.substring(firstDotIndex + 1).replace(/\./g, '');
        }
        
        // Add the inch symbol back
        if (cleanValue) {
            e.target.value = cleanValue + '"';
        } else {
            e.target.value = '';
        }
        
        // Restore cursor position (before the inch symbol)
        // Make sure cursor doesn't go past the number part
        const maxCursorPosition = cleanValue.length;
        const newPosition = Math.min(cursorPosition, maxCursorPosition);
        e.target.setSelectionRange(newPosition, newPosition);
    });

    input.addEventListener('click', (e) => {
        // Prevent cursor from being placed after the inch symbol
        const value = e.target.value.replace(/"/g, '');
        const maxPosition = value.length;
        
        setTimeout(() => {
            if (e.target.selectionStart > maxPosition) {
                e.target.setSelectionRange(maxPosition, maxPosition);
            }
        }, 0);
    });

    input.addEventListener('keydown', (e) => {
        // Prevent arrow right from going past the number
        if (e.key === 'ArrowRight') {
            const value = e.target.value.replace(/"/g, '');
            const maxPosition = value.length;
            
            if (e.target.selectionStart >= maxPosition) {
                e.preventDefault();
            }
        }
    });

    input.addEventListener('blur', (e) => {
        // Ensure inch symbol is present on blur
        let value = e.target.value.replace(/"/g, '');
        if (value) {
            e.target.value = value + '"';
        }
    });
}

document.addEventListener('DOMContentLoaded', function() {
    initializeTextHeightInput();
});

// ------------------------------------------------------------------------------------------------------------
// PICTOGRAM OVERLAY FUNCTIONALITY
// ------------------------------------------------------------------------------------------------------------

// Function to show pictogram overlay on product media
function initializePictogramOverlay() {
    const pictogramDropdown = document.querySelector('[data-pictogram-dropdown]');
    const productMediaWrapper = document.querySelector('.product__media-list-wrapper');

    if (!pictogramDropdown || !productMediaWrapper) {
        return;
    }

    // Listen for pictogram option changes
    const pictogramOptions = pictogramDropdown.querySelectorAll('[data-pictogram-option]');

    pictogramOptions.forEach(option => {
        option.addEventListener('click', function() {
            const radioInput = this.querySelector('input[type="radio"]');
            if (radioInput && radioInput.checked) {
                showPictogramOverlay(this, productMediaWrapper);
            }
        });
    });
}

// Function to show the pictogram overlay
function showPictogramOverlay(selectedOption, mediaWrapper) {
    removePictogramOverlay();

    const pictogramImage = selectedOption.querySelector('.pictogram-option-icon');
    if (!pictogramImage) {
        return;
    }

    // Create overlay container
    const overlay = document.createElement('div');
    overlay.className = 'pictogram-overlay';
    overlay.setAttribute('data-pictogram-overlay', 'true');

    // Clone the image and create the overlay content
    const overlayImage = pictogramImage.cloneNode(true);
    overlayImage.className = 'pictogram-overlay-image';
    overlayImage.setAttribute('width', '64');
    overlayImage.setAttribute('height', '64');

    overlay.appendChild(overlayImage);

    mediaWrapper.style.position = 'relative';
    mediaWrapper.appendChild(overlay);
}

function removePictogramOverlay() {
    const existingOverlay = document.querySelector('[data-pictogram-overlay]');
    if (existingOverlay) {
        existingOverlay.remove();
    }
}

document.addEventListener('DOMContentLoaded', function() {
    setTimeout(initializePictogramOverlay, 500);
});

// ============================================================================================================
// QUANTITY DISCOUNTS TABLE - DECORATION PRICE UPDATES
// ============================================================================================================

/**
 * Convert price value to cents
 * Handles both formats: values >= 100 (divide by 100 to get dollars, then * 100 for cents)
 * and values < 100 (already in cents format)
 * @param {number} priceValue - The price value from data attribute
 * @returns {number} Price in cents
 */
function convertPriceToCents(priceValue) {
    if (!priceValue || priceValue === 0) return 0;
    
    // If value is >= 100, it's in the format: value / 100 = dollars, dollars * 100 = cents
    // e.g., 2000 / 100 = 20 dollars = 2000 cents
    if (priceValue >= 100) {
        return Math.round((priceValue / 100) * 100);
    }
    
    // If value is < 100, it's already in cents
    // e.g., 40 = 40 cents
    return Math.round(priceValue);
}

function getPriceAttributeNumber(element, displayAttribute, retailAttribute, priceMode = 'display') {
    if (!element) {
        return 0;
    }

    const preferredAttribute = priceMode === 'retail' ? retailAttribute : displayAttribute;
    const rawValue = element.getAttribute(preferredAttribute) ?? element.getAttribute(displayAttribute);
    const parsedValue = parseFloat(rawValue);

    return Number.isFinite(parsedValue) ? parsedValue : 0;
}

/**
 * Calculate the price from width/height dimensions (data-price-display value)
 * This calculates: width * height * pricePerSquareInch
 * @param {'display'|'retail'} priceMode - Which price source to use for the selected method
 * @returns {number} Price in cents, or 0 if not applicable
 */
function calculateDimensionPrice(priceMode = 'display') {
    const widthInput = document.querySelector('[data-width-input], [data-width-input-color]');
    const heightInput = document.querySelector('[data-height-input], [data-height-input-color]');
    const selectedDecorationMethodInput = document.querySelector('[data-decoration-method-radio]:checked');

    if (!widthInput || !heightInput || !selectedDecorationMethodInput) {
        return 0;
    }

    const width = parseFloat(widthInput.value) || 0;
    const height = parseFloat(heightInput.value) || 0;
    const pricePerSquareInch = getPriceAttributeNumber(
        selectedDecorationMethodInput,
        'data-method-card-price',
        'data-method-card-price-retail',
        priceMode
    );
    const method = selectedDecorationMethodInput.getAttribute('data-method');
    
    // Only calculate if method is not 'Blank' and dimensions are provided
    if (method === 'Blank' || width === 0 || height === 0 || pricePerSquareInch === 0) {
        return 0;
    }
    
    const squareInches = width * height;
    const totalPrice = squareInches * pricePerSquareInch;
    
    // Convert to cents (totalPrice is in dollars, multiply by 100 to get cents)
    // This matches the calculation in updatePrice() function
    if (totalPrice > 0) {
        return Math.round(totalPrice * 100);
    }
    
    return 0;
}

function hasDecorationDimensionPriceInputs() {
    const widthInput = document.querySelector('[data-width-input], [data-width-input-color]');
    const heightInput = document.querySelector('[data-height-input], [data-height-input-color]');

    return Boolean(widthInput || heightInput);
}

function getPersonalizationFeeConfig(priceMode = 'display') {
    const feeElement = document.querySelector('[data-personalization-fee]');
    if (!feeElement) {
        return { variantId: null, priceCents: 0 };
    }

    const priceCents = Math.round(getPriceAttributeNumber(
        feeElement,
        'data-personalization-fee-cents',
        'data-personalization-fee-retail-cents',
        priceMode
    ));
    const variantId = feeElement.getAttribute('data-personalization-fee-variant-id');

    return {
        variantId: variantId ? variantId.toString() : null,
        priceCents: Number.isFinite(priceCents) ? priceCents : 0
    };
}

function getCustomizationFeeConfig(priceMode = 'display') {
    const feeElement = document.querySelector('[data-customization-fee]');
    if (!feeElement) {
        return { variantId: null, priceCents: 0 };
    }

    const priceCents = Math.round(getPriceAttributeNumber(
        feeElement,
        'data-customization-fee-cents',
        'data-customization-fee-retail-cents',
        priceMode
    ));
    const variantId = feeElement.getAttribute('data-customization-fee-variant-id');

    return {
        variantId: variantId ? variantId.toString() : null,
        priceCents: Number.isFinite(priceCents) ? priceCents : 0
    };
}

function isPersonalizationFeeEligible() {
    if (isCurrentVariantBespokeEnabled()) {
        return false;
    }

    const selectedDecorationMethodInput = document.querySelector('input[data-method-card-price]:checked');
    if (!selectedDecorationMethodInput) {
        return false;
    }

    const method = selectedDecorationMethodInput.getAttribute('data-method');
    return Boolean(method && method !== 'Blank');
}

function isCustomizationFeeEligible() {
    const csvPostPurchaseInput = document.querySelector('[data-csv-post-purchase-input]');
    if (csvPostPurchaseInput && !csvPostPurchaseInput.disabled && csvPostPurchaseInput.value) {
        return true;
    }

    if (typeof allCsvFiles !== 'undefined' && Array.isArray(allCsvFiles) && allCsvFiles.length > 0) {
        return true;
    }

    const csvFileInput = document.querySelector('[data-csv-file-input]');
    if (csvFileInput?.files?.length > 0) {
        return true;
    }

    const csvInputs = document.querySelectorAll('[data-product-decoration-modal] input[data-dynamic-file="true"][data-file-type="csv"]');
    return csvInputs.length > 0;
}

/**
 * Calculate total additional price from all decoration selections
 * @param {'display'|'retail'} priceMode - Which addon pricing source to use
 * @returns {number} Total additional price in cents
 */
function calculateDecorationAdditionalPrice(priceMode = 'display') {
    let perUnitAdditionalPrice = 0;
    let oneTimeAdditionalPrice = 0;

    const personalizationFee = getPersonalizationFeeConfig(priceMode);
    const customizationFee = getCustomizationFeeConfig(priceMode);

    if (isPersonalizationFeeEligible() && personalizationFee.priceCents > 0) {
        oneTimeAdditionalPrice += personalizationFee.priceCents;
    }

    if (isCustomizationFeeEligible() && customizationFee.priceCents > 0) {
        perUnitAdditionalPrice += customizationFee.priceCents;
    }

    // Get embellishment option price (radio - only one selected)
    // The data-price-increase is on the label, and data-embellishment-price is on the input
    const selectedEmbellishmentInput = document.querySelector('input[data-embellishment-price]:checked');
    if (selectedEmbellishmentInput) {
        // Try to get from the label's data-price-increase first, then fallback to input's data-embellishment-price
        const label = selectedEmbellishmentInput.closest('[data-price-increase]');
        const embellishmentPrice = getPriceAttributeNumber(
            label,
            'data-price-increase',
            'data-price-increase-retail',
            priceMode
        ) || getPriceAttributeNumber(
            selectedEmbellishmentInput,
            'data-embellishment-price',
            'data-embellishment-price-retail',
            priceMode
        );
        perUnitAdditionalPrice += convertPriceToCents(embellishmentPrice);
    }

    // Get packaging option price (radio - only one selected)
    // The data-price-increase is on the label, and data-packaging-price is on the input
    const selectedPackagingInput = document.querySelector('input[data-packaging-price]:checked');
    if (selectedPackagingInput) {
        // Try to get from the label's data-price-increase first, then fallback to input's data-packaging-price
        const label = selectedPackagingInput.closest('[data-price-increase]');
        const packagingPrice = getPriceAttributeNumber(
            label,
            'data-price-increase',
            'data-price-increase-retail',
            priceMode
        ) || getPriceAttributeNumber(
            selectedPackagingInput,
            'data-packaging-price',
            'data-packaging-price-retail',
            priceMode
        );
        perUnitAdditionalPrice += convertPriceToCents(packagingPrice);
    }

    // Get more embellishments prices (checkboxes - multiple can be selected)
    const checkedMoreEmbellishments = document.querySelectorAll('input[data-price-increase-checkbox]:checked');
    checkedMoreEmbellishments.forEach(checkbox => {
        const moreEmbellishmentPrice = getPriceAttributeNumber(
            checkbox,
            'data-price-increase-checkbox',
            'data-price-increase-checkbox-retail',
            priceMode
        );
        perUnitAdditionalPrice += convertPriceToCents(moreEmbellishmentPrice);
    });

    // Get dimension-based price (width * height * pricePerSquareInch from data-price-display)
    const dimensionPrice = calculateDimensionPrice(priceMode);
    if (dimensionPrice > 0) {
        perUnitAdditionalPrice += dimensionPrice;
    }

    // For bespoke products without decoration dimension inputs, price from custom sizing dimensions.
    if (dimensionPrice === 0 && !hasDecorationDimensionPriceInputs()) {
        const customSizingSection = document.querySelector('[data-product-custom-sizing]');
        const isBespokeVisible = customSizingSection && !customSizingSection.hasAttribute('hidden');
        if (isBespokeVisible) {
            const decorationMethodInput = document.querySelector('[data-decoration-method-radio]:checked');
            const method = decorationMethodInput?.getAttribute('data-method');
            if (decorationMethodInput && method && method !== 'Blank') {
                const decorationPricePerSqIn = getPriceAttributeNumber(
                    decorationMethodInput,
                    'data-method-card-price',
                    'data-method-card-price-retail',
                    priceMode
                );
                const w = parseFloat(document.querySelector('[data-custom-sizing-width]')?.value) || 0;
                const h = parseFloat(document.querySelector('[data-custom-sizing-height]')?.value) || 0;
                if (decorationPricePerSqIn > 0 && w > 0 && h > 0) {
                    perUnitAdditionalPrice += Math.round(decorationPricePerSqIn * w * h * 100);
                }
            }
        }
    }

    return {
        perUnitPriceCents: perUnitAdditionalPrice,
        oneTimePriceCents: oneTimeAdditionalPrice
    };
}


/**
 * Update quantity discounts table when decoration selections change
 */
function updateQuantityDiscountsTableFromDecoration() {
    // Get current variant from the variant picker's script tag (more reliable than form input)
    const variantPicker = document.querySelector('.product__info variant-picker') || document.querySelector('variant-picker');
    if (!variantPicker) return;

    const variantScript = variantPicker.querySelector('script[data-variant]');
    if (!variantScript) return;

    let currentVariant;
    try {
        currentVariant = JSON.parse(variantScript.textContent);
    } catch (e) {
        console.error('Error parsing variant data:', e);
        return;
    }

    if (!currentVariant || !currentVariant.id) return;

    // Use the variant object directly
    updateQuantityDiscountsTable(currentVariant);
}

function refreshBespokePricingForCurrentSelection() {
    updateCustomSizingPerItemCost();
    updateQuantityDiscountsTableFromDecoration();
    updateProductMetaMinimumBespokePriceDisplay();
}

let bespokePricingRefreshFrame = null;

function scheduleBespokePricingRefresh() {
    if (bespokePricingRefreshFrame !== null) {
        cancelAnimationFrame(bespokePricingRefreshFrame);
    }

    bespokePricingRefreshFrame = requestAnimationFrame(() => {
        bespokePricingRefreshFrame = null;
        refreshBespokePricingForCurrentSelection();
    });
}

function bindProductRerenderPricingRefresh() {
    document.querySelectorAll('product-rerender[observe-form]').forEach((rerenderElement) => {
        const formId = rerenderElement.getAttribute('observe-form');
        const form = formId ? document.forms[formId] : null;

        if (!form || form.dataset.bespokePricingRerenderBound === 'true') {
            return;
        }

        form.dataset.bespokePricingRerenderBound = 'true';
        form.addEventListener('product:rerender', function() {
            scheduleBespokePricingRefresh();
        });
    });
}

// Event listeners for decoration price changes
document.addEventListener('DOMContentLoaded', function() {
    // Hide price label and container by default on page load
    const priceLabelElement = document.querySelector('[data-table-price-label]');
    const priceContainerElement = document.querySelector('[data-table-price]');
    if (priceLabelElement) {
        priceLabelElement.style.display = 'none';
    }
    if (priceContainerElement) {
        priceContainerElement.style.display = 'none';
    }

    bindProductRerenderPricingRefresh();
    scheduleBespokePricingRefresh();

    // Use event delegation to listen for all changes
    document.addEventListener('change', function(e) {
        const target = e.target;

        if (target.matches('[data-csv-post-purchase-toggle], [data-csv-file-input]')) {
            updateQuantityDiscountsTableFromDecoration();
            return;
        }

        // Check if it's a decoration method radio button
        if (target.matches('input[data-method-card-price]')) {
            updateQuantityDiscountsTableFromDecoration();
            return;
        }

        // Check if it's an embellishment option radio button
        if (target.matches('input[data-embellishment-price]') ||
            target.closest('[data-embellishment-option][data-price-increase]')) {
            updateQuantityDiscountsTableFromDecoration();
            return;
        }

        // Check if it's a packaging option radio button
        if (target.matches('input[data-packaging-price]') ||
            target.closest('[data-packaging-option][data-price-increase]')) {
            updateQuantityDiscountsTableFromDecoration();
            return;
        }

        // Check if it's a more embellishment checkbox
        if (target.matches('input[data-price-increase-checkbox]')) {
            updateQuantityDiscountsTableFromDecoration();
            return;
        }
    });

    // Listen for width/height input changes to update table when dimensions change
    document.addEventListener('input', function(e) {
        if (e.target.matches('[data-width-input], [data-height-input], [data-width-input-color], [data-height-input-color]')) {
            updateQuantityDiscountsTableFromDecoration();
        }

        // Listen for quantity input changes to update the price display
        if (e.target.matches('input[name="quantity"]')) {
            scheduleBespokePricingRefresh();
        }
    });

    // Listen for quantity change events (when using buttons or direct input)
    document.addEventListener('change', function(e) {
        if (e.target.matches('input[name="quantity"]')) {
            scheduleBespokePricingRefresh();
        }
    });

    // Also listen for clicks on labels to handle cases where clicking the label triggers the change
    document.addEventListener('click', function(e) {
        const target = e.target.closest('label[data-price-increase], label[data-method-card-price]');
        if (target) {
            updateQuantityDiscountsTableFromDecoration();
        }
    });
});
